import i18next from "i18next";
import {commonConfig} from "../../common/js/config";
import {escapeHtml} from "../../common/js/html";
import {renderReusablePackageSelector} from "../../common/js/olx-placement-package-ui";

export const PLACEMENT_OPERATION_ACTIVATE_ADS = 'activate_ads';
export const PLACEMENT_OPERATION_PUBLISH_DRAFTS = 'publish_drafts';
export const PLACEMENT_OPERATION_BUMP_ADS = 'bump_ads';
export const PLACEMENT_OPERATION_ADVERTISE_ADS = 'advertise_ads';
export const PLACEMENT_RESOURCE_BUMP_FEATURE = 'bump_feature';
export const PLACEMENT_RESOURCE_ADVERTISING_FEATURE = 'advertising_feature';
export const PLACEMENT_STATUS_READY = 'ready';
export const PLACEMENT_STATUS_NEEDS_PACKAGE = 'needs_package';
export const PLACEMENT_STATUS_INVALID_STATUS = 'invalid_status';
export const PLACEMENT_STATUS_API_ERROR = 'api_error';
export const PLACEMENT_STATUS_EXCLUDED = 'excluded';
const PLACEMENT_STATUS_CHECKING = 'checking';
export const PLACEMENT_PURCHASE_SINGLE_AD = 'single_ad';
export const PLACEMENT_PURCHASE_CATEGORY_PACKET = 'category_packet';
export const PLACEMENT_PURCHASE_MEGA_PACKET = 'mega_packet';
export const SCHEDULED_PLACEMENT_STRATEGY_USE_AVAILABLE = 'use_available';
export const SCHEDULED_PLACEMENT_STRATEGY_PACKAGE = 'scheduled_package';
export const SCHEDULED_PLACEMENT_STRATEGY_SINGLE_PLACEMENT = 'single_placement';
export const SCHEDULED_PLACEMENT_STRATEGY_UNPAID = 'unpaid';
export const SCHEDULED_PLACEMENT_STRATEGY_EXCLUDED = 'excluded';
export const SCHEDULED_CATEGORY_STRATEGY_MANUAL = 'manual';
export const SCHEDULED_PACKAGE_SELECTION_AUTO = 'auto';
export const SCHEDULED_PACKAGE_SELECTION_FIXED = 'fixed';
const ADVERTISING_OPTION_ORDER = ['light_start', 'fast_sale', 'turbo_sale'];

const placementStatusLabel = (status, item = null) => {
    if (status === PLACEMENT_STATUS_INVALID_STATUS && item?.reason_code?.startsWith('draft_not_publishable')) {
        return i18next.t('ads:placementPlan.draftIncomplete', {defaultValue: 'Not ready'});
    }
    const labels = {
        [PLACEMENT_STATUS_READY]: i18next.t('ads:placementPlan.ready', {defaultValue: 'Ready'}),
        [PLACEMENT_STATUS_NEEDS_PACKAGE]: i18next.t('ads:placementPlan.needsPackage', {defaultValue: 'Needs package'}),
        [PLACEMENT_STATUS_INVALID_STATUS]: i18next.t('ads:placementPlan.invalidStatus', {defaultValue: 'Invalid status'}),
        [PLACEMENT_STATUS_API_ERROR]: i18next.t('ads:placementPlan.apiError', {defaultValue: 'API error'}),
        [PLACEMENT_STATUS_EXCLUDED]: i18next.t('ads:placementPlan.excluded', {defaultValue: 'Excluded'}),
        [PLACEMENT_STATUS_CHECKING]: i18next.t('ads:placementPlan.checkingShort', {defaultValue: 'Loading'}),
    };
    return labels[status] || status;
};

const renderPlacementExcludeIcon = () => `
    <svg class="placement-plan-exclude-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M19,7a1,1,0,0,0-1,1V19.191A1.92,1.92,0,0,1,15.99,21H8.01A1.92,1.92,0,0,1,6,19.191V8A1,1,0,0,0,4,8V19.191A3.918,3.918,0,0,0,8.01,23h7.98A3.918,3.918,0,0,0,20,19.191V8A1,1,0,0,0,19,7Z" fill="currentColor"></path>
        <path d="M20,4H16V2a1,1,0,0,0-1-1H9A1,1,0,0,0,8,2V4H4A1,1,0,0,0,4,6H20a1,1,0,0,0,0-2ZM10,4V3h4V4Z" fill="currentColor"></path>
        <path d="M11,17V10a1,1,0,0,0-2,0v7a1,1,0,0,0,2,0Z" fill="currentColor"></path>
        <path d="M15,17V10a1,1,0,0,0-2,0v7a1,1,0,0,0,2,0Z" fill="currentColor"></path>
    </svg>
`;

const placementReasonLabel = (item) => {
    if ([PLACEMENT_STATUS_NEEDS_PACKAGE, PLACEMENT_STATUS_EXCLUDED].includes(item?.status)) {
        return '';
    }
    const reasonCode = item?.reason_code;
    const reasonParams = item?.reason_params || {};
    if (!reasonCode) {
        return item?.reason || '';
    }
    return i18next.t(`ads:placementReasons.${reasonCode}`, {
        defaultValue: item?.reason || reasonCode,
        ...reasonParams,
    });
};

const placementWarningLabel = (category) => {
    const warningCode = category?.warning_code;
    const warningParams = category?.warning_params || {};
    if (!warningCode) {
        return category?.warning || '';
    }
    return i18next.t(`ads:placementReasons.${warningCode}`, {
        defaultValue: category?.warning || warningCode,
        ...warningParams,
    });
};

export const placementItemMatchesFilter = (item, filter) => {
    if (filter === 'all') {
        return true;
    }
    if (filter === 'problems') {
        return [
            PLACEMENT_STATUS_NEEDS_PACKAGE,
            PLACEMENT_STATUS_INVALID_STATUS,
            PLACEMENT_STATUS_API_ERROR,
        ].includes(item?.status);
    }
    if (filter === 'ready') {
        return item?.status === PLACEMENT_STATUS_READY;
    }
    if (filter === 'needs_package') {
        return item?.status === PLACEMENT_STATUS_NEEDS_PACKAGE;
    }
    if (filter === 'errors') {
        return [
            PLACEMENT_STATUS_INVALID_STATUS,
            PLACEMENT_STATUS_API_ERROR,
        ].includes(item?.status);
    }
    if (filter === 'excluded') {
        return item?.status === PLACEMENT_STATUS_EXCLUDED;
    }
    return true;
};

const formatMoney = (money) => {
    if (!money || money.amount === null || money.amount === undefined || money.amount === '') {
        return '-';
    }
    const amount = Number(money.amount);
    const formatted = Number.isFinite(amount)
        ? amount.toLocaleString(undefined, {maximumFractionDigits: 2})
        : String(money.amount);
    const currency = commonConfig.currencies[money.currency] || money.currency;
    return `${formatted}${currency ? ` ${currency}` : ''}${money.mixed ? '+' : ''}`;
};

const renderEstimatedCostSuffix = (money, resourcePreviewPending = false) => {
    if (resourcePreviewPending || !money || money.amount === null || money.amount === undefined || money.amount === '') {
        return '';
    }
    return ` · ${escapeHtml(i18next.t('ads:placementPlan.estimatedCostShort', {defaultValue: 'Cost'}))}: ${escapeHtml(formatMoney(money))}`;
};

const renderCompactList = (items, emptyLabel) => {
    if (!items?.length) {
        return `<span class="placement-plan-muted">${escapeHtml(emptyLabel)}</span>`;
    }
    return `
        <div class="placement-plan-chip-list">
            ${items.map((item) => `<span>${escapeHtml(item)}</span>`).join('')}
        </div>
    `;
};

const renderSummaryFilterCard = ({filter, label, value}, activeFilter, resourcePreviewPending = false) => `
    <button
        type="button"
        class="placement-plan-summary-card placement-plan-summary-filter placement-plan-filter ${activeFilter === filter ? 'active' : ''}"
        data-placement-filter="${escapeHtml(filter)}"
    >
        <span>${escapeHtml(label)}</span>
        <strong>${resourcePreviewPending && filter !== 'all'
            ? '<span class="placement-plan-summary-skeleton" aria-hidden="true"></span>'
            : Number(value || 0)}</strong>
    </button>
`;

const isPaidFeatureResourceKind = (resourceKind) => [
    'paid_feature',
    PLACEMENT_RESOURCE_BUMP_FEATURE,
    PLACEMENT_RESOURCE_ADVERTISING_FEATURE,
].includes(resourceKind);

const isAdvertisingResourceKind = (resourceKind) => resourceKind === PLACEMENT_RESOURCE_ADVERTISING_FEATURE;

const getAdvertisingOptionLabel = (option) => {
    const optionKey = option?.option_key || '';
    const labels = {
        light_start: i18next.t('ads:paidFeatures.lightStart', {defaultValue: 'Light start'}),
        fast_sale: i18next.t('ads:paidFeatures.fastSale', {defaultValue: 'Fast sale'}),
        turbo_sale: i18next.t('ads:paidFeatures.turboSale', {defaultValue: 'Turbo sale'}),
    };
    return labels[optionKey] || option?.label || option?.name || option?.code || optionKey;
};

const getAdvertisingSelectionValue = (option) => option?.selection_code || option?.code || option?.option_key || '';

const advertisingSelectionMatchesOption = (selectionValue, option) => {
    const selected = String(selectionValue || '');
    if (!selected || !option) {
        return false;
    }
    return [
        String(option.selection_code || ''),
        String(option.code || ''),
        String(option.option_key || ''),
    ].includes(selected);
};

const getAdvertisingSelectedValueForItem = (item, selections = {}) => {
    const explicitValue = selections?.[item?.item_key] || '';
    if (explicitValue) {
        return explicitValue;
    }
    const selectedOption = (item?.paid_feature_options || []).find((option) => option?.selected);
    if (selectedOption) {
        return getAdvertisingSelectionValue(selectedOption);
    }
    const paidFeature = item?.paid_feature || null;
    if (!paidFeature) {
        return '';
    }
    const matchedOption = (item?.paid_feature_options || []).find((option) => (
        advertisingSelectionMatchesOption(paidFeature.selection_code, option)
        || advertisingSelectionMatchesOption(paidFeature.code, option)
        || advertisingSelectionMatchesOption(paidFeature.option_key, option)
    ));
    return getAdvertisingSelectionValue(matchedOption || paidFeature);
};

const formatAdvertisingOptionPrice = (option) => {
    const fallback = i18next.t('ads:paidFeatures.priceAtPurchaseShort', {defaultValue: 'цена OLX'});
    if (!option?.price) {
        return fallback;
    }
    const formatted = option.price.formatted || formatMoney(option.price);
    return formatted && formatted !== '-' ? formatted : fallback;
};

const renderSummary = (plan, activeFilter, resourcePreviewPending = false) => {
    const cards = isPaidFeatureResourceKind(plan?.resource_kind) ? [
        {
            filter: 'all',
            label: i18next.t('ads:placementPlan.selected', {defaultValue: 'Selected'}),
            value: plan.total_selected,
        },
        {
            filter: 'ready',
            label: i18next.t('ads:placementPlan.readyToRun', {defaultValue: 'Ready'}),
            value: plan.total_ready,
        },
        {
            filter: 'errors',
            label: i18next.t('ads:paidFeatures.unavailable', {defaultValue: 'Unavailable'}),
            value: plan.total_errors,
        },
        {
            filter: 'excluded',
            label: i18next.t('ads:placementPlan.excluded', {defaultValue: 'Excluded'}),
            value: plan.total_excluded,
        },
    ] : [
        {
            filter: 'all',
            label: i18next.t('ads:placementPlan.selected', {defaultValue: 'Selected'}),
            value: plan.total_selected,
        },
        {
            filter: 'ready',
            label: i18next.t('ads:placementPlan.readyToRun', {defaultValue: 'Ready'}),
            value: plan.total_ready,
        },
        {
            filter: 'needs_package',
            label: i18next.t('ads:placementPlan.filterNeedsPackage', {defaultValue: 'Require payment'}),
            value: plan.total_needs_package,
        },
        {
            filter: 'errors',
            label: i18next.t('ads:placementPlan.errors', {defaultValue: 'Errors'}),
            value: plan.total_errors,
        },
        {
            filter: 'excluded',
            label: i18next.t('ads:placementPlan.excluded', {defaultValue: 'Excluded'}),
            value: plan.total_excluded,
        },
    ];
    return `
    <div class="placement-plan-summary">
        <div class="placement-plan-summary-filters">
            ${cards.map((card) => renderSummaryFilterCard(card, activeFilter, resourcePreviewPending)).join('')}
        </div>
    </div>
`;
};

export const getSinglePlacementSelectionKey = (selection = {}) => [
    String(selection.payment_method || ''),
    String(selection.id || selection.packet_id || ''),
    selection.is_premium ? 'premium' : 'standard',
].join('|');

const getSinglePlacementOptionKind = (option) => (
    option?.is_premium ? 'premium' : 'start'
);

const getSinglePlacementOptionsByItemKey = (options = []) => {
    const byItemKey = {};
    (options || []).forEach((option) => {
        (option.covered_item_keys || []).forEach((itemKey) => {
            const key = String(itemKey || '');
            if (!key) {
                return;
            }
            if (!byItemKey[key]) {
                byItemKey[key] = [];
            }
            byItemKey[key].push(option);
        });
    });
    return byItemKey;
};

const getActionColumnOrder = (isDraftPublish, isScheduled = false, hasScheduledPackageAction = false) => (
    isScheduled
        ? (
            isDraftPublish
                ? ['use_available', 'unpaid', ...(hasScheduledPackageAction ? ['scheduled_package'] : []), 'start', 'premium']
                : ['use_available', ...(hasScheduledPackageAction ? ['scheduled_package'] : []), 'start', 'premium']
        )
        : (isDraftPublish ? ['unpaid', 'start', 'premium'] : ['start', 'premium'])
);

const getSinglePlacementOptionByKind = (options, kind) => (
    (options || []).find((option) => getSinglePlacementOptionKind(option) === kind) || null
);

const getScheduledCategoryStrategy = (categoryKey, options = {}) => (
    options.scheduledCategoryStrategies?.[categoryKey] || ''
);

const getScheduledPackageSelectionMode = (categoryKey, options = {}) => (
    options.scheduledPackageSelectionModes?.[categoryKey] || SCHEDULED_PACKAGE_SELECTION_AUTO
);

const renderPlacementActionSelectOption = ({value, label, selected, itemKey = '', categoryKey = '', accountKey = ''}) => `
    <li
        class="integrations-select__option placement-plan-action-select-option ${selected ? 'is-selected' : ''}"
        role="option"
        aria-selected="${selected ? 'true' : 'false'}"
        data-placement-action-value="${escapeHtml(value)}"
        ${itemKey ? `data-placement-item-key="${escapeHtml(itemKey)}"` : ''}
        ${categoryKey ? `data-placement-category-key="${escapeHtml(categoryKey)}"` : ''}
        ${accountKey ? `data-placement-account-key="${escapeHtml(accountKey)}"` : ''}
    >
        ${escapeHtml(label)}
    </li>
`;

const renderPlacementActionSelect = ({
    selectKey,
    openKey = '',
    label,
    selectedLabel,
    optionsHtml,
    itemKey = '',
    categoryKey = '',
    accountKey = '',
    className = '',
}) => `
    <div
        class="integrations-select placement-plan-action-select ${openKey === selectKey ? 'is-open' : ''} ${className}"
        data-placement-action-select="${escapeHtml(selectKey)}"
        ${itemKey ? `data-placement-item-key="${escapeHtml(itemKey)}"` : ''}
        ${categoryKey ? `data-placement-category-key="${escapeHtml(categoryKey)}"` : ''}
        ${accountKey ? `data-placement-account-key="${escapeHtml(accountKey)}"` : ''}
    >
        <button
            type="button"
            class="integrations-select__toggle"
            aria-haspopup="listbox"
            aria-expanded="${openKey === selectKey ? 'true' : 'false'}"
            data-placement-action-select-toggle="${escapeHtml(selectKey)}"
            ${itemKey ? `data-placement-item-key="${escapeHtml(itemKey)}"` : ''}
            ${categoryKey ? `data-placement-category-key="${escapeHtml(categoryKey)}"` : ''}
            ${accountKey ? `data-placement-account-key="${escapeHtml(accountKey)}"` : ''}
        >
            <span class="integrations-select__label">${escapeHtml(selectedLabel)}</span>
            <span class="integrations-select__arrow" aria-hidden="true"></span>
        </button>
        <div class="integrations-select__dropdown" role="presentation">
            <ul class="integrations-select__options" role="listbox" aria-label="${escapeHtml(label)}">
                ${optionsHtml}
            </ul>
        </div>
    </div>
`;

const getAdvertisingStatusLabel = (item) => {
    if (item.status === PLACEMENT_STATUS_INVALID_STATUS && item.reason_code === 'paid_feature_option_required') {
        return i18next.t('ads:paidFeatures.optionRequiredBadge', {defaultValue: 'Choose option'});
    }
    return placementStatusLabel(item.status, item);
};

const getAdvertisingStatusClass = (item) => (
    item.status === PLACEMENT_STATUS_INVALID_STATUS && item.reason_code === 'paid_feature_option_required'
        ? 'option_required'
        : item.status
);

const renderAdvertisingItemOption = (item, option, selectedValue) => {
    if (!option) {
        return '<span class="placement-plan-item-action-cell is-empty"></span>';
    }
    const selectionValue = getAdvertisingSelectionValue(option);
    const checked = Boolean(option.selected) || advertisingSelectionMatchesOption(selectedValue, option);
    const disabled = option.available === false || !selectionValue || item.status === PLACEMENT_STATUS_EXCLUDED;
    const reason = option.reason || option.reason_code || '';
    const label = formatAdvertisingOptionPrice(option);
    return `
        <label
            class="placement-plan-item-action-check placement-plan-paid-feature-check ${checked ? 'active' : ''} ${disabled ? 'disabled' : ''}"
            title="${escapeHtml(disabled ? reason : getAdvertisingOptionLabel(option))}"
        >
            <input
                type="checkbox"
                class="form-check-input ogo-checkbox placement-plan-item-action-check-input"
                data-placement-item-key="${escapeHtml(item.item_key)}"
                data-placement-action-value="paid_feature:${escapeHtml(selectionValue)}"
                ${checked ? 'checked' : ''}
                ${disabled ? 'disabled' : ''}
            >
            <span class="placement-plan-paid-feature-price">${escapeHtml(label)}</span>
        </label>
    `;
};

const isAdvertisingOptionSelectable = (option) => Boolean(
    option
    && option.available !== false
    && getAdvertisingSelectionValue(option)
);

const getAdvertisingActionSelectLabel = (option) => (
    `${getAdvertisingOptionLabel(option)} ${formatAdvertisingOptionPrice(option)}`.trim()
);

const renderAdvertisingItemActionSelect = (item, selectedValue, options = {}) => {
    const availableOptions = ADVERTISING_OPTION_ORDER
        .map((optionKey) => (item.paid_feature_options || []).find((option) => option.option_key === optionKey))
        .filter(isAdvertisingOptionSelectable);
    if (!availableOptions.length) {
        return '';
    }
    const selectedOption = availableOptions.find((option) => (
        advertisingSelectionMatchesOption(selectedValue, option)
    ));
    const selectOptions = [
        renderPlacementActionSelectOption({
            value: '',
            label: i18next.t('ads:placementPlan.noActionSelected', {defaultValue: 'Select action'}),
            selected: !selectedOption,
            itemKey: item.item_key,
        }),
        ...availableOptions.map((option) => renderPlacementActionSelectOption({
            value: `paid_feature:${getAdvertisingSelectionValue(option)}`,
            label: getAdvertisingActionSelectLabel(option),
            selected: advertisingSelectionMatchesOption(selectedValue, option),
            itemKey: item.item_key,
        })),
    ];
    return renderPlacementActionSelect({
        selectKey: `advertising-item:${item.item_key}`,
        openKey: options.actionSelectOpenKey || '',
        label: i18next.t('ads:paidFeatures.advertisingOptions', {defaultValue: 'Advertising options'}),
        selectedLabel: selectedOption
            ? getAdvertisingActionSelectLabel(selectedOption)
            : i18next.t('ads:placementPlan.noActionSelected', {defaultValue: 'Select action'}),
        optionsHtml: selectOptions.join(''),
        itemKey: item.item_key,
        className: 'placement-plan-item-action-select',
    });
};

const renderAdvertisingItem = (item, allowExclude, options = {}) => {
    const selectedValue = getAdvertisingSelectedValueForItem(item, options.advertisingItemSelections || {});
    const optionByKey = new Map((item.paid_feature_options || []).map((option) => [option.option_key, option]));
    const resourcePreviewPending = Boolean(options.resourcePreviewPending);
    const canExclude = !resourcePreviewPending && allowExclude && item.status !== PLACEMENT_STATUS_EXCLUDED;
    const canRestore = !resourcePreviewPending && allowExclude && item.status === PLACEMENT_STATUS_EXCLUDED;
    const reasonLabel = item.reason_code === 'paid_feature_option_required' ? '' : placementReasonLabel(item);
    const statusClass = getAdvertisingStatusClass(item);
    const statusLabel = getAdvertisingStatusLabel(item);
    const itemClasses = [
        'placement-plan-item',
        'placement-plan-item--preflight',
        resourcePreviewPending ? 'is-resource-pending' : '',
        item.status === PLACEMENT_STATUS_EXCLUDED ? 'is-excluded' : '',
        selectedValue ? 'is-single-ad-target' : '',
    ].filter(Boolean).join(' ');
    return `
        <div class="${escapeHtml(itemClasses)}" data-placement-item-key="${escapeHtml(item.item_key)}">
            ${renderPlacementThumbnail(item)}
            <div class="placement-plan-item-main">
                <div class="placement-plan-item-title">${escapeHtml(item.title || item.ad_id || item.item_key)}</div>
                <div class="placement-plan-item-meta">
                    ${item.ad_id ? `OLX ID: ${escapeHtml(item.ad_id)}` : ''}
                    ${item.current_status ? ` · ${escapeHtml(item.current_status)}` : ''}
                </div>
                ${reasonLabel ? `<div class="placement-plan-item-reason">${escapeHtml(reasonLabel)}</div>` : ''}
                <div class="placement-plan-item-status-inline">
                    <span class="placement-plan-status placement-plan-status--${escapeHtml(statusClass)}">${escapeHtml(statusLabel)}</span>
                </div>
            </div>
            <div class="placement-plan-item-actions placement-plan-item-actions--advertising">
                <div class="placement-plan-item-status-slot">
                    <span class="placement-plan-status placement-plan-status--${escapeHtml(statusClass)}">${escapeHtml(statusLabel)}</span>
                </div>
                <div class="placement-plan-item-action-options placement-plan-advertising-options" aria-label="${escapeHtml(i18next.t('ads:paidFeatures.advertisingOptions', {defaultValue: 'Advertising options'}))}">
                    ${ADVERTISING_OPTION_ORDER.map((optionKey) => (
                        renderAdvertisingItemOption(item, optionByKey.get(optionKey), selectedValue)
                    )).join('')}
                </div>
                <div class="placement-plan-action-select-wrap">
                    ${renderAdvertisingItemActionSelect(item, selectedValue, options)}
                </div>
                <div class="placement-plan-item-exclude-slot">
                    ${canExclude ? `
                        <button
                            type="button"
                            class="placement-plan-exclude-item placement-plan-exclude-item--subtle placement-plan-exclude-item--icon delete-icon-button"
                            data-placement-item-key="${escapeHtml(item.item_key)}"
                            aria-label="${escapeHtml(i18next.t('ads:placementPlan.exclude', {defaultValue: 'Exclude'}))}"
                            title="${escapeHtml(i18next.t('ads:placementPlan.exclude', {defaultValue: 'Exclude'}))}"
                        >
                            ${renderPlacementExcludeIcon()}
                        </button>
                    ` : ''}
                    ${canRestore ? `
                        <button type="button" class="placement-plan-restore-item placement-plan-exclude-item--subtle" data-placement-item-key="${escapeHtml(item.item_key)}">
                            ${i18next.t('ads:placementPlan.restore', {defaultValue: 'Restore'})}
                        </button>
                    ` : ''}
                </div>
            </div>
        </div>
    `;
};

const renderItem = (item, allowExclude, options = {}) => {
    if (isAdvertisingResourceKind(options.resourceKind)) {
        return renderAdvertisingItem(item, allowExclude, options);
    }
    const singlePlacementOptions = (options.singlePlacementOptions || []).filter((option) => (
        (option.covered_item_keys || []).includes(item.item_key)
    ));
    const isScheduled = Boolean(options.isScheduled);
    const isExternalScheduledActivation = (
        isScheduled
        && options.operationType === PLACEMENT_OPERATION_ACTIVATE_ADS
        && item.created_by_service !== true
    );
    const categoryStrategy = options.scheduledCategoryStrategy || '';
    const resourcePreviewPending = Boolean(options.resourcePreviewPending);
    const canChooseAction = !isExternalScheduledActivation && !resourcePreviewPending && allowExclude && (
        isScheduled
            ? ![
                PLACEMENT_STATUS_API_ERROR,
                PLACEMENT_STATUS_INVALID_STATUS,
                PLACEMENT_STATUS_EXCLUDED,
            ].includes(item.status)
            : item.status === PLACEMENT_STATUS_NEEDS_PACKAGE
    );
    const isDraftPublish = options.operationType === PLACEMENT_OPERATION_PUBLISH_DRAFTS;
    const isUnpaidDraftSelected = (options.unpaidDraftItemKeysSet || new Set()).has(item.item_key);
    const selectedSinglePlacement = options.singlePlacementSelections?.[item.item_key] || null;
    const scheduledStrategy = options.scheduledItemStrategies?.[item.item_key] || '';
    const effectiveScheduledStrategy = isExternalScheduledActivation
        ? SCHEDULED_PLACEMENT_STRATEGY_USE_AVAILABLE
        : scheduledStrategy || (
        isScheduled
        && categoryStrategy
        && categoryStrategy !== SCHEDULED_CATEGORY_STRATEGY_MANUAL
        && item.status !== PLACEMENT_STATUS_EXCLUDED
        && ![PLACEMENT_STATUS_API_ERROR, PLACEMENT_STATUS_INVALID_STATUS].includes(item.status)
            ? categoryStrategy
            : ''
        );
    const selectedSinglePlacementKey = selectedSinglePlacement
        ? getSinglePlacementSelectionKey(selectedSinglePlacement)
        : '';
    const selectedActionValue = resourcePreviewPending && item.status !== PLACEMENT_STATUS_EXCLUDED
        ? ''
        : item.status === PLACEMENT_STATUS_EXCLUDED
        ? 'exclude'
        : selectedSinglePlacement
            ? `single:${selectedSinglePlacementKey}`
            : isUnpaidDraftSelected
                ? 'unpaid'
                : effectiveScheduledStrategy === SCHEDULED_PLACEMENT_STRATEGY_UNPAID
                    ? 'unpaid'
                    : effectiveScheduledStrategy === SCHEDULED_PLACEMENT_STRATEGY_USE_AVAILABLE
                        ? 'use_available'
                        : effectiveScheduledStrategy === SCHEDULED_PLACEMENT_STRATEGY_PACKAGE
                            ? 'scheduled_package'
                            : '';
    const actionBadge = resourcePreviewPending && item.status !== PLACEMENT_STATUS_EXCLUDED
        ? placementStatusLabel(PLACEMENT_STATUS_CHECKING)
        : item.status === PLACEMENT_STATUS_EXCLUDED
        ? placementStatusLabel(PLACEMENT_STATUS_EXCLUDED)
        : isExternalScheduledActivation
            ? i18next.t('ads:placementPlan.directActivation', {defaultValue: 'Direct activation'})
        : selectedSinglePlacement
            ? i18next.t('ads:placementPlan.buyAtLaunch', {defaultValue: 'Buy at launch'})
            : isUnpaidDraftSelected
                ? i18next.t('ads:placementPlan.applyUnpaidToVisible', {defaultValue: 'No purchase'})
                : effectiveScheduledStrategy === SCHEDULED_PLACEMENT_STRATEGY_UNPAID
                    ? i18next.t('ads:placementPlan.applyUnpaidToVisible', {defaultValue: 'No purchase'})
                    : effectiveScheduledStrategy === SCHEDULED_PLACEMENT_STRATEGY_USE_AVAILABLE
                        ? i18next.t('ads:placementPlan.useSlotsAtLaunchShort', {defaultValue: 'Use'})
                        : effectiveScheduledStrategy === SCHEDULED_PLACEMENT_STRATEGY_PACKAGE
                            ? i18next.t('ads:placementPlan.buyPackageAtLaunchShort', {defaultValue: 'Buy'})
                            : placementStatusLabel(item.status, item);
    const actionBadgeStatus = resourcePreviewPending && item.status !== PLACEMENT_STATUS_EXCLUDED
        ? PLACEMENT_STATUS_CHECKING
        : item.status === PLACEMENT_STATUS_EXCLUDED
        ? PLACEMENT_STATUS_EXCLUDED
        : isExternalScheduledActivation
            ? PLACEMENT_STATUS_READY
        : selectedSinglePlacement
            ? 'single-placement'
            : isUnpaidDraftSelected
                ? 'unpaid-draft'
                : effectiveScheduledStrategy === SCHEDULED_PLACEMENT_STRATEGY_UNPAID
                    ? 'unpaid-draft'
                    : effectiveScheduledStrategy === SCHEDULED_PLACEMENT_STRATEGY_USE_AVAILABLE
                        ? 'single-placement'
                        : effectiveScheduledStrategy === SCHEDULED_PLACEMENT_STRATEGY_PACKAGE
                            ? 'single-placement'
                            : item.status;
    const canExclude = !resourcePreviewPending && allowExclude && item.status !== PLACEMENT_STATUS_EXCLUDED;
    const canRestore = !resourcePreviewPending && allowExclude && item.status === PLACEMENT_STATUS_EXCLUDED;
    const shouldReserveActionColumns = canChooseAction || item.status === PLACEMENT_STATUS_EXCLUDED;
    const itemClasses = [
        'placement-plan-item',
        'placement-plan-item--preflight',
        resourcePreviewPending ? 'is-resource-pending' : '',
        item.status === PLACEMENT_STATUS_EXCLUDED ? 'is-excluded' : '',
        isExternalScheduledActivation ? 'is-direct-activation' : '',
        selectedActionValue && selectedActionValue !== 'exclude' ? 'is-single-ad-target' : '',
    ].filter(Boolean).join(' ');
    const hasScheduledPackageAction = isScheduled && Boolean(options.scheduledPackageSelection);
    const actionColumnOrder = isExternalScheduledActivation
        ? []
        : getActionColumnOrder(isDraftPublish, isScheduled, hasScheduledPackageAction);
    const useActionSelect = actionColumnOrder.length > 3;
    const getItemActionLabel = (actionKind, option = null) => {
        if (actionKind === 'unpaid') {
            return i18next.t('ads:placementPlan.applyUnpaidToVisible', {defaultValue: 'No purchase'});
        }
        const label = actionKind === 'premium'
            ? i18next.t('ads:placementPlan.applyPremiumToVisible', {defaultValue: 'Premium'})
            : i18next.t('ads:placementPlan.applyStartToVisible', {defaultValue: 'Start'});
        return option ? `${label} ${formatPlacementPacketPrice(option)}` : label;
    };
    const renderItemActionCell = (actionKind) => {
        if (!canChooseAction) {
            return '<span class="placement-plan-item-action-cell is-empty"></span>';
        }
        if (actionKind === 'use_available') {
            return `
                <label class="placement-plan-item-action-check ${selectedActionValue === 'use_available' ? 'active' : ''}">
                    <input
                        type="checkbox"
                        class="form-check-input ogo-checkbox placement-plan-item-action-check-input"
                        data-placement-item-key="${escapeHtml(item.item_key)}"
                        data-placement-action-value="use_available"
                        ${selectedActionValue === 'use_available' ? 'checked' : ''}
                    >
                </label>
            `;
        }
        if (actionKind === 'unpaid') {
            if (!isDraftPublish || !item.draft_id) {
                return '<span class="placement-plan-item-action-cell is-empty"></span>';
            }
            return `
                <label class="placement-plan-item-action-check ${selectedActionValue === 'unpaid' ? 'active' : ''}">
                    <input
                        type="checkbox"
                        class="form-check-input ogo-checkbox placement-plan-item-action-check-input"
                        data-placement-item-key="${escapeHtml(item.item_key)}"
                        data-placement-action-value="unpaid"
                        ${selectedActionValue === 'unpaid' ? 'checked' : ''}
                    >
                </label>
            `;
        }
        if (actionKind === 'scheduled_package') {
            if (!hasScheduledPackageAction) {
                return '<span class="placement-plan-item-action-cell is-empty"></span>';
            }
            return `
                <label class="placement-plan-item-action-check ${selectedActionValue === 'scheduled_package' ? 'active' : ''}">
                    <input
                        type="checkbox"
                        class="form-check-input ogo-checkbox placement-plan-item-action-check-input"
                        data-placement-item-key="${escapeHtml(item.item_key)}"
                        data-placement-action-value="scheduled_package"
                        ${selectedActionValue === 'scheduled_package' ? 'checked' : ''}
                    >
                </label>
            `;
        }
        const option = getSinglePlacementOptionByKind(singlePlacementOptions, actionKind);
        if (!option) {
            return '<span class="placement-plan-item-action-cell is-empty"></span>';
        }
        const optionKey = getSinglePlacementSelectionKey(option);
        const optionValue = `single:${optionKey}`;
        return `
            <label class="placement-plan-item-action-check ${selectedActionValue === optionValue ? 'active' : ''}">
                <input
                    type="checkbox"
                    class="form-check-input ogo-checkbox placement-plan-item-action-check-input"
                    data-placement-item-key="${escapeHtml(item.item_key)}"
                    data-placement-action-value="${escapeHtml(optionValue)}"
                    data-placement-payment-method="${escapeHtml(option.payment_method || '')}"
                    data-placement-packet-id="${escapeHtml(option.id || '')}"
                    data-placement-is-premium="${option.is_premium ? '1' : '0'}"
                    ${selectedActionValue === optionValue ? 'checked' : ''}
                >
            </label>
        `;
    };
    const renderItemActionSelectOptions = () => {
        if (!canChooseAction) {
            return '';
        }
        const itemKey = item.item_key;
        const selectOptions = [
            renderPlacementActionSelectOption({
                value: '',
                label: i18next.t('ads:placementPlan.noActionSelected', {defaultValue: 'Select action'}),
                selected: !selectedActionValue,
                itemKey,
            }),
        ];
        actionColumnOrder.forEach((actionKind) => {
            if (actionKind === 'use_available') {
                selectOptions.push(renderPlacementActionSelectOption({
                    value: 'use_available',
                    label: i18next.t('ads:placementPlan.useSlotsAtLaunch', {defaultValue: 'Use slots at launch'}),
                    selected: selectedActionValue === 'use_available',
                    itemKey,
                }));
                return;
            }
            if (actionKind === 'unpaid') {
                if (isDraftPublish && item.draft_id) {
                    selectOptions.push(renderPlacementActionSelectOption({
                        value: 'unpaid',
                        label: getItemActionLabel(actionKind),
                        selected: selectedActionValue === 'unpaid',
                        itemKey,
                    }));
                }
                return;
            }
            if (actionKind === 'scheduled_package') {
                if (hasScheduledPackageAction) {
                    selectOptions.push(renderPlacementActionSelectOption({
                        value: 'scheduled_package',
                        label: i18next.t('ads:placementPlan.buyPackageAtLaunch', {defaultValue: 'Buy package at launch'}),
                        selected: selectedActionValue === 'scheduled_package',
                        itemKey,
                    }));
                }
                return;
            }
            const option = getSinglePlacementOptionByKind(singlePlacementOptions, actionKind);
            if (!option) {
                return;
            }
            const optionValue = `single:${getSinglePlacementSelectionKey(option)}`;
            selectOptions.push(renderPlacementActionSelectOption({
                value: optionValue,
                label: getItemActionLabel(actionKind, option),
                selected: selectedActionValue === optionValue,
                itemKey,
            }));
        });
        return selectOptions.join('');
    };
    const itemActionSelectLabel = i18next.t('ads:placementPlan.action', {defaultValue: 'Action'});
    const itemActionSelectedLabel = selectedActionValue
        ? selectedActionValue === 'unpaid'
            ? getItemActionLabel('unpaid')
            : selectedActionValue === 'use_available'
                ? i18next.t('ads:placementPlan.useSlotsAtLaunch', {defaultValue: 'Use slots at launch'})
                : selectedActionValue === 'scheduled_package'
                    ? i18next.t('ads:placementPlan.buyPackageAtLaunch', {defaultValue: 'Buy package at launch'})
                    : getItemActionLabel(
                        selectedSinglePlacement?.is_premium ? 'premium' : 'start',
                        selectedSinglePlacement,
                    )
        : i18next.t('ads:placementPlan.noActionSelected', {defaultValue: 'Select action'});
    return `
        <div class="${escapeHtml(itemClasses)}" data-placement-item-key="${escapeHtml(item.item_key)}">
            ${renderPlacementThumbnail(item)}
            <div class="placement-plan-item-main">
                <div class="placement-plan-item-title">${escapeHtml(item.title || item.ad_id || item.draft_id || item.item_key)}</div>
                <div class="placement-plan-item-meta">
                    ${item.ad_id ? `OLX ID: ${escapeHtml(item.ad_id)}` : ''}
                    ${item.draft_id ? `${i18next.t('ads:workspace.draftId', {defaultValue: 'Draft ID'})}: ${escapeHtml(item.draft_id)}` : ''}
                    ${item.current_status ? ` · ${escapeHtml(item.current_status)}` : ''}
                </div>
                ${placementReasonLabel(item) ? `<div class="placement-plan-item-reason">${escapeHtml(placementReasonLabel(item))}</div>` : ''}
            </div>
            <div class="placement-plan-item-actions ${useActionSelect ? 'placement-plan-actions--select-mode' : ''}">
                <div class="placement-plan-item-status-slot">
                    <span class="placement-plan-status placement-plan-status--${escapeHtml(actionBadgeStatus)} ${isExternalScheduledActivation ? 'placement-plan-direct-activation' : ''}">${escapeHtml(actionBadge)}</span>
                </div>
                <div class="placement-plan-item-action-options ${shouldReserveActionColumns ? '' : 'is-empty'}" aria-label="${escapeHtml(i18next.t('ads:placementPlan.action', {defaultValue: 'Action'}))}">
                    ${actionColumnOrder.map(renderItemActionCell).join('')}
                </div>
                <div class="placement-plan-action-select-wrap ${canChooseAction ? '' : 'is-empty'}">
                    ${canChooseAction ? renderPlacementActionSelect({
                        selectKey: `item:${item.item_key}`,
                        openKey: options.actionSelectOpenKey || '',
                        label: itemActionSelectLabel,
                        selectedLabel: itemActionSelectedLabel,
                        optionsHtml: renderItemActionSelectOptions(),
                        itemKey: item.item_key,
                        className: 'placement-plan-item-action-select',
                    }) : ''}
                </div>
                <div class="placement-plan-item-exclude-slot">
                    ${canExclude ? `
                        <button
                            type="button"
                            class="placement-plan-exclude-item placement-plan-exclude-item--subtle placement-plan-exclude-item--icon delete-icon-button"
                            data-placement-item-key="${escapeHtml(item.item_key)}"
                            aria-label="${escapeHtml(i18next.t('ads:placementPlan.exclude', {defaultValue: 'Exclude'}))}"
                            title="${escapeHtml(i18next.t('ads:placementPlan.exclude', {defaultValue: 'Exclude'}))}"
                        >
                            ${renderPlacementExcludeIcon()}
                        </button>
                    ` : ''}
                    ${canRestore ? `
                        <button type="button" class="placement-plan-restore-item placement-plan-exclude-item--subtle" data-placement-item-key="${escapeHtml(item.item_key)}">
                            ${i18next.t('ads:placementPlan.restore', {defaultValue: 'Restore'})}
                        </button>
                    ` : ''}
                </div>
            </div>
        </div>
    `;
};

const renderMasterActionCheck = ({categoryKey, actionValue, label, checked}) => `
    <label class="placement-plan-master-action-check ${checked ? 'active' : ''}">
        <span class="placement-plan-master-action-input-slot">
            <input
                type="checkbox"
                class="form-check-input ogo-checkbox placement-plan-master-action-check-input"
                data-placement-category-key="${escapeHtml(categoryKey)}"
                data-placement-master-action="${escapeHtml(actionValue)}"
                ${checked ? 'checked' : ''}
            >
        </span>
        <span class="placement-plan-master-action-label">${escapeHtml(label)}</span>
    </label>
`;

const getAdvertisingMasterOptionStates = (actionableItems, optionByKey, selections = {}) => (
    ADVERTISING_OPTION_ORDER.map((optionKey) => {
        const option = optionByKey.get(optionKey);
        if (!option) {
            return {optionKey, option: null, applicableItems: [], disabled: true, checked: false};
        }
        const applicableItems = actionableItems.filter((item) => (
            (item.paid_feature_options || []).some((itemOption) => (
                itemOption.option_key === optionKey && itemOption.available !== false
            ))
        ));
        const disabled = !applicableItems.length;
        const checked = applicableItems.length > 0
            && applicableItems.every((item) => {
                const itemOption = (item.paid_feature_options || []).find((candidate) => (
                    candidate.option_key === optionKey && candidate.available !== false
                ));
                return advertisingSelectionMatchesOption(
                    getAdvertisingSelectedValueForItem(item, selections),
                    itemOption,
                );
            });
        return {optionKey, option, applicableItems, disabled, checked};
    })
);

const getAdvertisingMasterSelectedActionValue = (optionStates) => {
    const selectedStates = optionStates.filter((state) => state.checked);
    return selectedStates.length === 1 ? `paid_feature:${selectedStates[0].optionKey}` : '';
};

const renderAdvertisingMasterActionSelectOptions = ({
    optionStates,
    selectedActionValue,
    categoryKey = '',
    accountKey = '',
}) => {
    const selectOptions = [
        renderPlacementActionSelectOption({
            value: '',
            label: i18next.t('ads:placementPlan.action', {defaultValue: 'Action'}),
            selected: !selectedActionValue,
            categoryKey,
            accountKey,
        }),
    ];
    optionStates
        .filter((state) => state.option && !state.disabled)
        .forEach((state) => {
            const actionValue = `paid_feature:${state.optionKey}`;
            selectOptions.push(renderPlacementActionSelectOption({
                value: actionValue,
                label: getAdvertisingOptionLabel(state.option),
                selected: selectedActionValue === actionValue,
                categoryKey,
                accountKey,
            }));
        });
    return selectOptions.join('');
};

const getAdvertisingMasterSelectedLabel = (optionStates, selectedActionValue) => {
    const selectedOptionKey = selectedActionValue.replace(/^paid_feature:/, '');
    const selectedState = optionStates.find((state) => state.optionKey === selectedOptionKey);
    return selectedState?.option
        ? getAdvertisingOptionLabel(selectedState.option)
        : i18next.t('ads:placementPlan.action', {defaultValue: 'Action'});
};

const renderAdvertisingMasterActions = (category, visibleItems, options = {}) => {
    const categoryKey = getPlacementCategoryKey(category);
    const actionableItems = (visibleItems || []).filter((item) => (
        ![PLACEMENT_STATUS_API_ERROR, PLACEMENT_STATUS_EXCLUDED].includes(item.status)
    ));
    if (!actionableItems.length || !options.allowExclude) {
        return '';
    }
    const selections = options.advertisingItemSelections || {};
    const optionByKey = new Map((category.advertising_options || category.paid_feature_options || []).map((option) => [option.option_key, option]));
    const optionStates = getAdvertisingMasterOptionStates(actionableItems, optionByKey, selections);
    const selectedActionValue = getAdvertisingMasterSelectedActionValue(optionStates);
    return `
        <div class="placement-plan-master-actions placement-plan-master-actions--advertising" data-placement-category-key="${escapeHtml(categoryKey)}">
            <span class="placement-plan-master-actions-label">
                ${escapeHtml(i18next.t('ads:placementPlan.visibleActions', {
                    count: actionableItems.length,
                    defaultValue: 'Apply to {{count}}:',
                }))}
            </span>
            <div class="placement-plan-item-action-options placement-plan-master-action-options placement-plan-advertising-options">
                ${optionStates.map(({optionKey, option, disabled, checked}) => {
                    if (!option) {
                        return '<span class="placement-plan-master-action-check is-empty"></span>';
                    }
                    return `
                        <label class="placement-plan-master-action-check ${checked ? 'active' : ''} ${disabled ? 'disabled' : ''}" title="${escapeHtml(disabled ? i18next.t('ads:paidFeatures.optionUnavailable', {defaultValue: 'Option unavailable'}) : getAdvertisingOptionLabel(option))}">
                            <span class="placement-plan-master-action-input-slot">
                                <input
                                    type="checkbox"
                                    class="form-check-input ogo-checkbox placement-plan-master-action-check-input"
                                    data-placement-category-key="${escapeHtml(categoryKey)}"
                                    data-placement-master-action="paid_feature:${escapeHtml(optionKey)}"
                                    ${checked ? 'checked' : ''}
                                    ${disabled ? 'disabled' : ''}
                                >
                            </span>
                            <span class="placement-plan-master-action-label">${escapeHtml(getAdvertisingOptionLabel(option))}</span>
                        </label>
                    `;
                }).join('')}
            </div>
            <div class="placement-plan-action-select-wrap placement-plan-master-action-select-wrap">
                ${renderPlacementActionSelect({
                    selectKey: `advertising-master:${categoryKey}`,
                    openKey: options.actionSelectOpenKey || '',
                    label: i18next.t('ads:placementPlan.visibleActions', {
                        count: actionableItems.length,
                        defaultValue: 'Apply to {{count}}:',
                    }),
                    selectedLabel: getAdvertisingMasterSelectedLabel(optionStates, selectedActionValue),
                    optionsHtml: renderAdvertisingMasterActionSelectOptions({
                        optionStates,
                        selectedActionValue,
                        categoryKey,
                    }),
                    categoryKey,
                    className: 'placement-plan-master-action-select',
                })}
            </div>
            <div class="placement-plan-master-exclude-slot">
                <button
                    type="button"
                    class="placement-plan-exclude-item placement-plan-exclude-item--subtle placement-plan-master-exclude-visible placement-plan-exclude-item--icon delete-icon-button"
                    data-placement-category-key="${escapeHtml(categoryKey)}"
                    aria-label="${escapeHtml(i18next.t('ads:placementPlan.excludeVisible', {defaultValue: 'Exclude'}))}"
                    title="${escapeHtml(i18next.t('ads:placementPlan.excludeVisible', {defaultValue: 'Exclude'}))}"
                >
                    ${renderPlacementExcludeIcon()}
                </button>
            </div>
        </div>
    `;
};

const getAdvertisingAccountCategories = (account) => (
    (account?.categories || []).filter((category) => isAdvertisingResourceKind(category?.resource_kind))
);

const isAdvertisingAccount = (account) => {
    const categories = account?.categories || [];
    return categories.length > 0 && categories.every((category) => isAdvertisingResourceKind(category?.resource_kind));
};

const getAdvertisingAccountItems = (account, activeFilter) => (
    getAdvertisingAccountCategories(account)
        .flatMap((category) => category.items || [])
        .filter((item) => placementItemMatchesFilter(item, activeFilter))
);

const getAdvertisingAccountOptionByKey = (account, items = []) => {
    const optionByKey = new Map();
    const addOption = (option) => {
        const key = option?.option_key;
        if (key && !optionByKey.has(key)) {
            optionByKey.set(key, option);
        }
    };
    getAdvertisingAccountCategories(account).forEach((category) => {
        (category.advertising_options || category.paid_feature_options || []).forEach(addOption);
    });
    (items || []).forEach((item) => {
        (item.paid_feature_options || []).forEach(addOption);
    });
    return optionByKey;
};

const renderAdvertisingAccountMeta = (account, resourcePreviewPending = false) => {
    if (resourcePreviewPending) {
        return `
            <span class="placement-plan-muted">
                ${escapeHtml(i18next.t('ads:placementPlan.checkingResources', {
                    defaultValue: 'Checking OLX placements...',
                }))}
            </span>
        `;
    }
    const paymentMethods = (account.payment_methods || [])
        .map((method) => method.label || method.value)
        .filter(Boolean);
    return `
        <span class="placement-plan-account-summary">
            ${escapeHtml(i18next.t('ads:placementPlan.accountStats', {
                defaultValue: 'Selected: {{selected}}, ready: {{ready}}, problems: {{problems}}',
                selected: account.total_selected || 0,
                ready: account.total_ready || 0,
                problems: Number(account.total_needs_package || 0) + Number(account.total_errors || 0),
            }))}${renderEstimatedCostSuffix(account.total_estimated_cost, resourcePreviewPending)}
        </span>
        <span class="placement-plan-account-chip">
            ${escapeHtml(i18next.t('ads:placementPlan.balance', {defaultValue: 'OLX balance'}))}: ${escapeHtml(formatMoney(account.olx_balance))}
        </span>
        <span class="placement-plan-account-chip">
            ${escapeHtml(i18next.t('ads:placementPlan.paymentMethods', {defaultValue: 'Payment methods'}))}: ${escapeHtml(paymentMethods.join(' / ') || i18next.t('ads:placementPlan.noPaymentMethods', {defaultValue: 'No payment methods'}))}
        </span>
    `;
};

const renderAdvertisingAccountMasterActions = (account, visibleItems, options = {}) => {
    if (options.resourcePreviewPending) {
        return '';
    }
    const accountKey = String(account.account_id || '');
    const actionableItems = (visibleItems || []).filter((item) => (
        ![PLACEMENT_STATUS_API_ERROR, PLACEMENT_STATUS_EXCLUDED].includes(item.status)
    ));
    if (!actionableItems.length || !options.allowExclude) {
        return '';
    }
    const selections = options.advertisingItemSelections || {};
    const optionByKey = getAdvertisingAccountOptionByKey(account, actionableItems);
    const optionStates = getAdvertisingMasterOptionStates(actionableItems, optionByKey, selections);
    const selectedActionValue = getAdvertisingMasterSelectedActionValue(optionStates);
    return `
        <div class="placement-plan-master-actions placement-plan-master-actions--advertising placement-plan-master-actions--advertising-account" data-placement-account-key="${escapeHtml(accountKey)}">
            <span class="placement-plan-master-actions-label">
                ${escapeHtml(i18next.t('ads:placementPlan.visibleActions', {
                    count: actionableItems.length,
                    defaultValue: 'Apply to {{count}}:',
                }))}
            </span>
            <div class="placement-plan-item-action-options placement-plan-master-action-options placement-plan-advertising-options">
                ${optionStates.map(({optionKey, option, disabled, checked}) => {
                    if (!option) {
                        return '<span class="placement-plan-master-action-check is-empty"></span>';
                    }
                    return `
                        <label class="placement-plan-master-action-check ${checked ? 'active' : ''} ${disabled ? 'disabled' : ''}" title="${escapeHtml(disabled ? i18next.t('ads:paidFeatures.optionUnavailable', {defaultValue: 'Option unavailable'}) : getAdvertisingOptionLabel(option))}">
                            <span class="placement-plan-master-action-input-slot">
                                <input
                                    type="checkbox"
                                    class="form-check-input ogo-checkbox placement-plan-master-action-check-input"
                                    data-placement-account-key="${escapeHtml(accountKey)}"
                                    data-placement-master-action="paid_feature:${escapeHtml(optionKey)}"
                                    ${checked ? 'checked' : ''}
                                    ${disabled ? 'disabled' : ''}
                                >
                            </span>
                            <span class="placement-plan-master-action-label">${escapeHtml(getAdvertisingOptionLabel(option))}</span>
                        </label>
                    `;
                }).join('')}
            </div>
            <div class="placement-plan-action-select-wrap placement-plan-master-action-select-wrap">
                ${renderPlacementActionSelect({
                    selectKey: `advertising-account-master:${accountKey}`,
                    openKey: options.actionSelectOpenKey || '',
                    label: i18next.t('ads:placementPlan.visibleActions', {
                        count: actionableItems.length,
                        defaultValue: 'Apply to {{count}}:',
                    }),
                    selectedLabel: getAdvertisingMasterSelectedLabel(optionStates, selectedActionValue),
                    optionsHtml: renderAdvertisingMasterActionSelectOptions({
                        optionStates,
                        selectedActionValue,
                        accountKey,
                    }),
                    accountKey,
                    className: 'placement-plan-master-action-select',
                })}
            </div>
            <div class="placement-plan-master-exclude-slot">
                <button
                    type="button"
                    class="placement-plan-exclude-item placement-plan-exclude-item--subtle placement-plan-master-exclude-visible placement-plan-exclude-item--icon delete-icon-button"
                    data-placement-account-key="${escapeHtml(accountKey)}"
                    aria-label="${escapeHtml(i18next.t('ads:placementPlan.excludeVisible', {defaultValue: 'Exclude'}))}"
                    title="${escapeHtml(i18next.t('ads:placementPlan.excludeVisible', {defaultValue: 'Exclude'}))}"
                >
                    ${renderPlacementExcludeIcon()}
                </button>
            </div>
        </div>
    `;
};

const renderAdvertisingAccount = (account, activeFilter, allowExclude, options = {}) => {
    const accountKey = String(account.account_id || '');
    const visibleItems = getAdvertisingAccountItems(account, activeFilter);
    if (!visibleItems.length) {
        return '';
    }
    const collapsed = options.collapsedPreflightAccountKeysSet?.has(accountKey);
    const resourcePreviewPending = Boolean(options.resourcePreviewPending);
    const toggleLabel = collapsed
        ? i18next.t('ads:placementSchedule.expandGroup', {defaultValue: 'Expand'})
        : i18next.t('ads:placementSchedule.collapseGroup', {defaultValue: 'Collapse'});
    const itemManagementLabel = i18next.t('ads:placementPlan.itemManagement', {
        count: visibleItems.length,
        defaultValue: 'Manage adverts ({{count}})',
    });
    return `
        <section class="placement-plan-account placement-plan-account--preflight placement-plan-account--advertising" data-placement-account-key="${escapeHtml(accountKey)}">
            <header class="placement-plan-preflight-group-header">
                <div class="placement-plan-account-heading">
                    <strong>${escapeHtml(account.account_name || account.account_id)}</strong>
                    <div class="placement-plan-account-meta-line">
                        ${renderAdvertisingAccountMeta(account, resourcePreviewPending)}
                    </div>
                </div>
                <button
                    type="button"
                    class="placement-plan-collapse-toggle placement-plan-toggle-account ${collapsed ? '' : 'is-expanded'}"
                    data-placement-account-key="${escapeHtml(accountKey)}"
                    aria-expanded="${collapsed ? 'false' : 'true'}"
                    aria-label="${escapeHtml(toggleLabel)}"
                    title="${escapeHtml(toggleLabel)}"
                >
                    ${renderPlacementChevronIcon()}
                </button>
            </header>
            ${collapsed ? '' : `
                <div class="placement-plan-item-management placement-plan-item-management--advertising-account">
                    <div class="placement-plan-advertising-account-items-heading">
                        <span>${escapeHtml(itemManagementLabel)}</span>
                    </div>
                    ${renderAdvertisingAccountMasterActions(account, visibleItems, {
                        ...options,
                        allowExclude,
                        advertisingItemSelections: options.advertisingItemSelections || {},
                    })}
                    <div class="placement-plan-items placement-plan-items--advertising-account">
                        ${visibleItems.map((item) => renderItem(item, allowExclude, {
                            resourceKind: PLACEMENT_RESOURCE_ADVERTISING_FEATURE,
                            operationType: options.operationType,
                            advertisingItemSelections: options.advertisingItemSelections || {},
                            actionSelectOpenKey: options.actionSelectOpenKey || '',
                            resourcePreviewPending,
                        })).join('')}
                    </div>
                </div>
            `}
        </section>
    `;
};

const renderMasterActions = (category, visibleItems, options = {}) => {
    if (options.resourcePreviewPending) {
        return '';
    }
    if (isAdvertisingResourceKind(category?.resource_kind)) {
        return renderAdvertisingMasterActions(category, visibleItems, options);
    }
    const categoryKey = getPlacementCategoryKey(category);
    const isScheduled = Boolean(options.isScheduled);
    const categoryStrategy = getScheduledCategoryStrategy(categoryKey, options);
    if (isScheduled && categoryStrategy !== SCHEDULED_CATEGORY_STRATEGY_MANUAL) {
        return '';
    }
    const actionItems = (visibleItems || []).filter((item) => (
        !(
            isScheduled
            && options.operationType === PLACEMENT_OPERATION_ACTIVATE_ADS
            && item.created_by_service !== true
        )
    )).filter((item) => (
        isScheduled
            ? ![PLACEMENT_STATUS_API_ERROR, PLACEMENT_STATUS_INVALID_STATUS].includes(item.status)
            : [PLACEMENT_STATUS_NEEDS_PACKAGE, PLACEMENT_STATUS_EXCLUDED].includes(item.status)
    ));
    if (!actionItems.length || !options.allowExclude) {
        return '';
    }

    const singleOptions = category.purchase_options?.single_ad_options || [];
    const singleOptionsByItemKey = getSinglePlacementOptionsByItemKey(singleOptions);
    const itemKeys = actionItems.map((item) => item.item_key).filter(Boolean);
    const singleSelections = options.singlePlacementSelections || {};
    const scheduledItemStrategies = options.scheduledItemStrategies || {};
    const unpaidDraftItemKeysSet = options.unpaidDraftItemKeysSet || new Set();
    const isDraftPublish = options.operationType === PLACEMENT_OPERATION_PUBLISH_DRAFTS;
    const hasScheduledPackageAction = isScheduled && Boolean(options.scheduledPackageSelection);
    const actionColumnOrder = getActionColumnOrder(isDraftPublish, isScheduled, hasScheduledPackageAction);
    const useActionSelect = actionColumnOrder.length > 3;
    const hasSingleKind = (kind) => itemKeys.some((itemKey) => (
        (singleOptionsByItemKey[itemKey] || []).some((option) => getSinglePlacementOptionKind(option) === kind)
    ));
    const allSingleKindSelected = (kind) => itemKeys.length > 0 && itemKeys.every((itemKey) => {
        const selection = singleSelections[itemKey];
        return selection && getSinglePlacementOptionKind(selection) === kind;
    });
    const allUnpaidSelected = itemKeys.length > 0 && itemKeys.every((itemKey) => unpaidDraftItemKeysSet.has(itemKey));
    const allUseAvailableSelected = itemKeys.length > 0 && itemKeys.every((itemKey) => (
        scheduledItemStrategies[itemKey] === SCHEDULED_PLACEMENT_STRATEGY_USE_AVAILABLE
    ));
    const allScheduledPackageSelected = itemKeys.length > 0 && itemKeys.every((itemKey) => (
        scheduledItemStrategies[itemKey] === SCHEDULED_PLACEMENT_STRATEGY_PACKAGE
    ));
    const allExcluded = itemKeys.length > 0 && actionItems.every((item) => item.status === PLACEMENT_STATUS_EXCLUDED);
    const selectedMasterActionValue = allUseAvailableSelected
        ? 'use_available'
        : allScheduledPackageSelected
            ? 'scheduled_package'
        : allUnpaidSelected
        ? 'unpaid'
        : allSingleKindSelected('start')
            ? 'single:start'
            : allSingleKindSelected('premium')
                ? 'single:premium'
                : '';
    const getMasterSinglePlacementLabel = (kind) => {
        const option = itemKeys
            .flatMap((itemKey) => singleOptionsByItemKey[itemKey] || [])
            .find((candidate) => getSinglePlacementOptionKind(candidate) === kind);
        const label = kind === 'premium'
            ? i18next.t('ads:placementPlan.applyPremiumToVisible', {defaultValue: 'Premium'})
            : i18next.t('ads:placementPlan.applyStartToVisible', {defaultValue: 'Start'});
        return option ? `${label} ${formatPlacementPacketPrice(option)}` : label;
    };
    const renderMasterActionSelectOptions = () => {
        const selectOptions = [
            renderPlacementActionSelectOption({
                value: '',
                label: i18next.t('ads:placementPlan.action', {defaultValue: 'Action'}),
                selected: !selectedMasterActionValue,
                categoryKey,
            }),
        ];
        actionColumnOrder.forEach((actionKind) => {
            if (actionKind === 'use_available') {
                selectOptions.push(renderPlacementActionSelectOption({
                    value: 'use_available',
                    label: i18next.t('ads:placementPlan.useSlotsAtLaunch', {defaultValue: 'Use slots at launch'}),
                    selected: selectedMasterActionValue === 'use_available',
                    categoryKey,
                }));
                return;
            }
            if (actionKind === 'unpaid') {
                if (isDraftPublish) {
                    selectOptions.push(renderPlacementActionSelectOption({
                        value: 'unpaid',
                        label: i18next.t('ads:placementPlan.applyUnpaidToVisible', {defaultValue: 'No purchase'}),
                        selected: selectedMasterActionValue === 'unpaid',
                        categoryKey,
                    }));
                }
                return;
            }
            if (actionKind === 'scheduled_package') {
                if (hasScheduledPackageAction) {
                    selectOptions.push(renderPlacementActionSelectOption({
                        value: 'scheduled_package',
                        label: i18next.t('ads:placementPlan.buyPackageAtLaunch', {defaultValue: 'Buy package at launch'}),
                        selected: selectedMasterActionValue === 'scheduled_package',
                        categoryKey,
                    }));
                }
                return;
            }
            if (!hasSingleKind(actionKind)) {
                return;
            }
            const value = `single:${actionKind}`;
            selectOptions.push(renderPlacementActionSelectOption({
                value,
                label: getMasterSinglePlacementLabel(actionKind),
                selected: selectedMasterActionValue === value,
                categoryKey,
            }));
        });
        return selectOptions.join('');
    };
    const masterActionSelectLabel = i18next.t('ads:placementPlan.visibleActions', {
        count: itemKeys.length,
        defaultValue: 'Apply to {{count}}:',
    });
    const masterActionSelectedLabel = selectedMasterActionValue
        ? selectedMasterActionValue === 'unpaid'
            ? i18next.t('ads:placementPlan.applyUnpaidToVisible', {defaultValue: 'No purchase'})
            : selectedMasterActionValue === 'use_available'
                ? i18next.t('ads:placementPlan.useSlotsAtLaunch', {defaultValue: 'Use slots at launch'})
                : selectedMasterActionValue === 'scheduled_package'
                    ? i18next.t('ads:placementPlan.buyPackageAtLaunch', {defaultValue: 'Buy package at launch'})
                    : getMasterSinglePlacementLabel(selectedMasterActionValue.split(':')[1] || '')
        : i18next.t('ads:placementPlan.action', {defaultValue: 'Action'});

    return `
        <div class="placement-plan-master-actions ${useActionSelect ? 'placement-plan-actions--select-mode' : ''}" data-placement-category-key="${escapeHtml(categoryKey)}">
            <span class="placement-plan-master-actions-label">
                ${escapeHtml(i18next.t('ads:placementPlan.visibleActions', {
                    count: itemKeys.length,
                    defaultValue: 'Apply to {{count}}:',
                }))}
            </span>
            <div class="placement-plan-item-action-options placement-plan-master-action-options">
                ${actionColumnOrder.map((actionKind) => {
                    if (actionKind === 'use_available') {
                        return renderMasterActionCheck({
                            categoryKey,
                            actionValue: 'use_available',
                            label: i18next.t('ads:placementPlan.useSlotsAtLaunch', {defaultValue: 'Use slots at launch'}),
                            checked: allUseAvailableSelected,
                        });
                    }
                    if (actionKind === 'unpaid') {
                        return isDraftPublish ? renderMasterActionCheck({
                            categoryKey,
                            actionValue: 'unpaid',
                            label: i18next.t('ads:placementPlan.applyUnpaidToVisible', {defaultValue: 'No purchase'}),
                            checked: allUnpaidSelected,
                        }) : '<span class="placement-plan-master-action-check is-empty"></span>';
                    }
                    if (actionKind === 'scheduled_package') {
                        return hasScheduledPackageAction ? renderMasterActionCheck({
                            categoryKey,
                            actionValue: 'scheduled_package',
                            label: i18next.t('ads:placementPlan.buyPackageAtLaunch', {defaultValue: 'Buy package at launch'}),
                            checked: allScheduledPackageSelected,
                        }) : '<span class="placement-plan-master-action-check is-empty"></span>';
                    }
                    if (!hasSingleKind(actionKind)) {
                        return '<span class="placement-plan-master-action-check is-empty"></span>';
                    }
                    return renderMasterActionCheck({
                        categoryKey,
                        actionValue: `single:${actionKind}`,
                        label: getMasterSinglePlacementLabel(actionKind),
                        checked: allSingleKindSelected(actionKind),
                    });
                }).join('')}
            </div>
            <div class="placement-plan-action-select-wrap placement-plan-master-action-select-wrap">
                ${renderPlacementActionSelect({
                    selectKey: `master:${categoryKey}`,
                    openKey: options.actionSelectOpenKey || '',
                    label: masterActionSelectLabel,
                    selectedLabel: masterActionSelectedLabel,
                    optionsHtml: renderMasterActionSelectOptions(),
                    categoryKey,
                    className: 'placement-plan-master-action-select',
                })}
            </div>
            <div class="placement-plan-master-exclude-slot">
                <button
                    type="button"
                    class="placement-plan-exclude-item placement-plan-exclude-item--subtle placement-plan-master-exclude-visible ${allExcluded ? '' : 'placement-plan-exclude-item--icon delete-icon-button'}"
                    data-placement-category-key="${escapeHtml(categoryKey)}"
                    aria-label="${escapeHtml(allExcluded
                        ? i18next.t('ads:placementPlan.restore', {defaultValue: 'Restore'})
                        : i18next.t('ads:placementPlan.excludeVisible', {defaultValue: 'Exclude'}))}"
                    title="${escapeHtml(allExcluded
                        ? i18next.t('ads:placementPlan.restore', {defaultValue: 'Restore'})
                        : i18next.t('ads:placementPlan.excludeVisible', {defaultValue: 'Exclude'}))}"
                >
                    ${allExcluded
                        ? escapeHtml(i18next.t('ads:placementPlan.restore', {defaultValue: 'Restore'}))
                        : renderPlacementExcludeIcon()}
                </button>
            </div>
        </div>
    `;
};

const getScheduleRowForItem = (item, scheduleRowsByKey) => (
    scheduleRowsByKey.get(item.item_key) || {
        key: item.item_key,
        title: item.title || item.ad_id || item.draft_id || item.item_key,
        scheduledAt: '',
        selected: false,
    }
);

const renderPlacementThumbnail = (item) => {
    const title = item.title || item.ad_id || item.draft_id || '';
    if (item.thumbnail_url) {
        return `
            <div class="placement-plan-thumb">
                <img src="${escapeHtml(item.thumbnail_url)}" alt="${escapeHtml(title)}">
            </div>
        `;
    }
    return `
        <div class="placement-plan-thumb placement-plan-thumb--empty" aria-hidden="true">
            ${escapeHtml(String(title || '?').trim().charAt(0).toUpperCase() || '?')}
        </div>
    `;
};

const getScheduleSelectionStats = (items, options) => {
    const rows = items.map((item) => getScheduleRowForItem(item, options.scheduleRowsByKey));
    const selected = rows.filter((row) => row.selected).length;
    return {
        selected,
        total: rows.length,
    };
};

const renderPlacementChevronIcon = () => `
    <svg class="placement-plan-chevron-icon" xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 128 128" aria-hidden="true" focusable="false">
        <path d="m64 88c-1.023 0-2.047-.391-2.828-1.172l-40-40c-1.563-1.563-1.563-4.094 0-5.656s4.094-1.563 5.656 0l37.172 37.172 37.172-37.172c1.563-1.563 4.094-1.563 5.656 0s1.563 4.094 0 5.656l-40 40c-.781.781-1.805 1.172-2.828 1.172z" fill="currentColor"></path>
    </svg>
`;

const renderPlacementOptimalIcon = () => `
    <svg class="placement-plan-optimal-icon" xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 511.999 511.999" aria-hidden="true" focusable="false">
        <path d="M15.34 224.948c-8.365 0-15.146 6.782-15.146 15.146v222.458c0 8.365 6.782 15.146 15.146 15.146h91.253v-252.75H15.34zM511.805 308.587c0-19.227-10.331-36.087-25.733-45.321a52.504 52.504 0 0 0 9.362-29.985c0-29.113-23.686-52.799-52.799-52.799H331.177c3.416-15.48 8.088-38.709 11.341-63.026 8.466-63.281 2.68-98.376-18.207-110.445-13.022-7.523-27.062-9.049-39.534-4.301-9.635 3.67-22.647 12.693-30.062 34.838l-29.294 76.701c-14.851 36.677-60.33 75.182-88.535 96.473V484.4C189.16 502.716 243.708 512 299.358 512h121.446c29.113 0 52.799-23.686 52.799-52.799a52.481 52.481 0 0 0-7.93-27.804c17.601-8.572 29.759-26.646 29.759-47.503a52.504 52.504 0 0 0-9.362-29.985c15.404-9.234 25.735-26.095 25.735-45.322z" fill="currentColor"></path>
    </svg>
`;

const renderScheduleItem = (item, options) => {
    const row = getScheduleRowForItem(item, options.scheduleRowsByKey);
    const scheduleControl = typeof options.renderScheduleControl === 'function'
        ? options.renderScheduleControl(row)
        : '';
    return `
        <div class="placement-plan-item placement-plan-item--schedule" data-placement-item-key="${escapeHtml(item.item_key)}">
            <label class="placement-schedule-inline-check">
                <input
                    type="checkbox"
                    class="form-check-input ogo-checkbox placement-schedule-row-check"
                    data-schedule-key="${escapeHtml(row.key)}"
                    ${row.selected ? 'checked' : ''}
                >
            </label>
            ${renderPlacementThumbnail(item)}
            <div class="placement-plan-item-main">
                <div class="placement-plan-item-title">${escapeHtml(item.title || item.ad_id || item.draft_id || item.item_key)}</div>
                <div class="placement-plan-item-meta">
                    ${item.ad_id ? `OLX ID: ${escapeHtml(item.ad_id)}` : ''}
                    ${item.draft_id ? `${i18next.t('ads:workspace.draftId', {defaultValue: 'Draft ID'})}: ${escapeHtml(item.draft_id)}` : ''}
                    ${item.current_status ? ` · ${escapeHtml(item.current_status)}` : ''}
                </div>
            </div>
            <div class="placement-schedule-inline-time">
                ${scheduleControl}
            </div>
        </div>
    `;
};

const renderPacketLabel = (packet) => {
    const parts = [
        packet.name || i18next.t('ads:placementPlan.packet', {defaultValue: 'Packet'}),
        packet.size ? i18next.t('ads:placementPlan.packetSize', {defaultValue: '{{size}} slots', size: packet.size}) : '',
        formatMoney(packet.price),
        packet.payment_method || '',
    ].filter(Boolean);
    return parts.join(' · ');
};

const formatPaidFeaturePrice = (feature) => {
    if (!feature?.price) {
        return i18next.t('ads:paidFeatures.priceAtPurchase', {defaultValue: 'OLX calculates at purchase'});
    }
    return formatMoney(feature.price);
};

const renderPaidFeatureSelector = (category) => {
    const options = category.paid_feature_options || [];
    const selectedCode = category.recommended_feature?.code || '';
    if (!options.length) {
        return `<div class="placement-plan-warning">${escapeHtml(category.purchase_error || i18next.t('ads:paidFeatures.noAvailableFeature', {defaultValue: 'No OLX bump feature is available.'}))}</div>`;
    }
    return `
        <div class="placement-plan-package-selector placement-plan-paid-feature-selector">
            <h4>${i18next.t('ads:paidFeatures.selectFeature', {defaultValue: 'Paid feature'})}</h4>
            <div class="placement-plan-package-options">
                ${options.map((feature) => {
                    const selected = String(feature.code || '') === String(selectedCode || '');
                    return `
                        <button
                            type="button"
                            class="placement-plan-package-card placement-plan-paid-feature-option ${selected ? 'active' : ''}"
                            data-placement-category-key="${escapeHtml(getPlacementCategoryKey(category))}"
                            data-paid-feature-code="${escapeHtml(feature.code || '')}"
                        >
                            <strong>${escapeHtml(feature.name || feature.code)}</strong>
                            <span>${escapeHtml(i18next.t('ads:paidFeatures.featureDuration', {
                                defaultValue: '{{duration}} days',
                                duration: feature.duration || '-',
                            }))}</span>
                            <b>${escapeHtml(formatPaidFeaturePrice(feature))}</b>
                            <small>${escapeHtml(selected
                                ? i18next.t('ads:placementPlan.selectedPackage', {defaultValue: 'Selected'})
                                : i18next.t('ads:placementPlan.selectPackage', {defaultValue: 'Select'}))}</small>
                        </button>
                    `;
                }).join('')}
            </div>
            ${category.price_unknown ? `
                <div class="placement-plan-warning">
                    ${escapeHtml(i18next.t('ads:paidFeatures.unknownPriceWarning', {
                        defaultValue: 'OLX did not return a pre-purchase price. The final price will be calculated by OLX when the operation runs.',
                    }))}
                </div>
            ` : ''}
        </div>
    `;
};

export const getPlacementCategoryKey = (category) => [
    category?.account_id ?? '',
    category?.category_id ?? '',
    category?.zone_id ?? '',
].map(String).join('|');

export const getPlacementPacketKey = (category, packet) => [
    category?.account_id ?? '',
    category?.category_id ?? '',
    category?.zone_id ?? '',
    packet?.purchase_kind ?? '',
    packet?.ad_id ?? '',
    packet?.draft_id ?? '',
    packet?.size ?? '',
    packet?.payment_method ?? '',
    packet?.id ?? '',
    packet?.type ?? 'base',
    packet?.packet_type ?? '',
    packet?.is_premium ? 'premium' : 'standard',
    packet?.name ?? '',
    packet?.price?.amount ?? '',
    packet?.price?.currency ?? '',
].map((value) => encodeURIComponent(String(value))).join('|');

export const getPlacementPurchaseOptions = (category) => {
    const purchaseOptions = category?.purchase_options || {};
    const options = [
        ...(purchaseOptions.single_ad_options || []),
        ...(purchaseOptions.category_packet_options || []),
        ...(purchaseOptions.mega_packet_options || []),
    ];
    if (options.length) {
        return options;
    }
    return (category?.available_packets || []).map((packet) => ({
        ...packet,
        purchase_kind: packet.purchase_kind || (String(packet.type || '').toLowerCase() === 'mega'
            ? PLACEMENT_PURCHASE_MEGA_PACKET
            : PLACEMENT_PURCHASE_CATEGORY_PACKET),
    }));
};

const getPlacementPurchaseOptionsByKind = (category, purchaseKind) => (
    getPlacementPurchaseOptions(category).filter((packet) => packet.purchase_kind === purchaseKind)
);

const getPacketPriceAmount = (packet) => {
    const amount = Number(packet?.price?.amount);
    return Number.isFinite(amount) ? amount : Number.MAX_SAFE_INTEGER;
};

const sortPackets = (packets) => [...(packets || [])].sort((a, b) => (
    Number(a.size || 0) - Number(b.size || 0)
    || getPacketPriceAmount(a) - getPacketPriceAmount(b)
    || String(a.name || '').localeCompare(String(b.name || ''))
));

const getQuantityThresholdSize = (sizeOptions, missingSlots) => (
    sizeOptions.find((size) => Number(size) >= Number(missingSlots || 0)) || null
);

const getPacketSizeOptions = (packets) => (
    [...new Set((packets || []).map((packet) => Number(packet.size || 0)).filter((size) => Number.isFinite(size) && size > 0))]
        .sort((a, b) => a - b)
);

const isStartPlacementPacket = (packet) => {
    const purchaseKind = String(packet?.purchase_kind || '');
    const packetType = String(packet?.packet_type || packet?.type || '').toLowerCase();
    return !packet?.is_premium
        && purchaseKind !== PLACEMENT_PURCHASE_MEGA_PACKET
        && packetType !== 'mega';
};

export const getPreferredPlacementPacketForSize = (category, size, paymentMethod = '', purchaseKind = '') => {
    const packets = sortPackets(getPlacementPurchaseOptions(category))
        .filter((packet) => !purchaseKind || packet.purchase_kind === purchaseKind)
        .filter((packet) => Number(packet.size || 0) === Number(size))
        .filter((packet) => !paymentMethod || String(packet.payment_method || '') === String(paymentMethod));
    return packets.find(isStartPlacementPacket) || packets[0] || null;
};

export const getDefaultPlacementPacket = (category) => {
    const packets = sortPackets(getPlacementPurchaseOptions(category));
    if (!packets.length) {
        return null;
    }
    const missingSlots = Number(category?.missing_slots || 0);
    const optimalSize = getQuantityThresholdSize(getPacketSizeOptions(packets), missingSlots);
    const preferredOptimal = optimalSize ? getPreferredPlacementPacketForSize(category, optimalSize) : null;
    if (preferredOptimal) {
        return preferredOptimal;
    }
    const coveringPackets = packets.filter((packet) => Number(packet.size || 0) >= missingSlots);
    return coveringPackets[0] || packets[0];
};

export const getPlacementPacketByKey = (category, packetKey) => (
    getPlacementPurchaseOptions(category).find((packet) => getPlacementPacketKey(category, packet) === packetKey) || null
);

export const getCheapestPlacementPacketForSize = (category, size, paymentMethod = '', purchaseKind = '') => {
    const packets = sortPackets(getPlacementPurchaseOptions(category))
        .filter((packet) => !purchaseKind || packet.purchase_kind === purchaseKind)
        .filter((packet) => Number(packet.size || 0) === Number(size))
        .filter((packet) => !paymentMethod || String(packet.payment_method || '') === String(paymentMethod));
    return packets[0] || null;
};

export const getDefaultPlacementPacketForKind = (category, purchaseKind) => {
    const packets = sortPackets(getPlacementPurchaseOptionsByKind(category, purchaseKind));
    if (!packets.length) {
        return null;
    }
    if (purchaseKind === PLACEMENT_PURCHASE_SINGLE_AD) {
        return packets[0];
    }
    const missingSlots = Number(category?.missing_slots || 0);
    const coveringPackets = packets.filter((packet) => Number(packet.size || 0) >= missingSlots);
    return coveringPackets[0] || packets[0];
};

export const getFirstMissingPlacementCategoryKey = (plan) => {
    for (const account of plan?.accounts || []) {
        for (const category of account.categories || []) {
            if (Number(category.missing_slots || 0) > 0) {
                return getPlacementCategoryKey(category);
            }
        }
    }
    return '';
};

const renderBalanceAfterPurchase = (balance, packet) => {
    const price = packet?.price;
    if (!balance || !price || balance.currency !== price.currency) {
        return '-';
    }
    const amount = Number(balance.amount) - Number(price.amount);
    if (!Number.isFinite(amount)) {
        return '-';
    }
    return formatMoney({amount, currency: balance.currency});
};

const formatPlacementPacketPrice = (packet) => (
    packet?.price
        ? `${packet.price_estimated ? '~' : ''}${formatMoney(packet.price)}`
        : i18next.t('ads:placementPlan.priceCalculatedByOlx', {defaultValue: 'Calculated by OLX at purchase'})
);

const getPacketVariantName = (packet) => {
    const name = packet?.name || '';
    if (packet?.purchase_kind === PLACEMENT_PURCHASE_MEGA_PACKET || String(packet?.packet_type || packet?.type || '').toLowerCase() === 'mega') {
        return i18next.t('ads:placementPlan.megaPackageVariant', {defaultValue: 'Mega'});
    }
    const quoted = name.match(/[ʼ'"]([^ʼ'"]+)[ʼ'"]/);
    if (quoted?.[1]) {
        return quoted[1];
    }
    if (name) {
        return name;
    }
    if (packet?.purchase_kind === PLACEMENT_PURCHASE_SINGLE_AD || Number(packet?.size || 0) === 1) {
        return packet?.is_premium
            ? i18next.t('ads:placementPlan.singleAdPremiumVariant', {defaultValue: 'Start Plus'})
            : i18next.t('ads:placementPlan.singleAdStartVariant', {defaultValue: 'Start'});
    }
    return i18next.t('ads:placementPlan.packetVariant', {defaultValue: 'Package'});
};

const getMegaCoverageLabels = (packet) => {
    const explicitLabels = (packet?.categories_labels || []).filter(Boolean);
    if (explicitLabels.length) {
        return explicitLabels;
    }
    const namePrefix = String(packet?.name || '').split('/')[0] || '';
    const cleaned = namePrefix
        .replace(/\s+мега\s*$/i, '')
        .replace(/\s+mega\s*$/i, '')
        .trim();
    if (!cleaned) {
        return [];
    }
    return cleaned
        .split(/\s+(?:та|и|і|and|&)\s+/i)
        .map((label) => label.trim())
        .filter(Boolean);
};

const getPaymentMethodLabel = (account, value) => {
    const method = (account.payment_methods || []).find((item) => String(item.value || '') === String(value || ''));
    return method?.label || value || '-';
};

const getSingleAdOptionAdvertLabel = (category, adId) => {
    const item = (category?.items || []).find((candidate) => String(candidate.ad_id || '') === String(adId || ''));
    return item?.title || item?.ad_id || adId || '-';
};

const getPackageHeadingLabel = (size) => (
    Number(size) === 1
        ? i18next.t('ads:placementPlan.chooseSingleAd', {defaultValue: 'Choose advert type'})
        : i18next.t('ads:placementPlan.choosePackage', {defaultValue: 'Choose package'})
);

const getCategoryDisplayName = (category, categoryDisplayNames = {}) => {
    const categoryId = String(category?.category_id ?? '');
    return categoryDisplayNames[categoryId]
        || category?.category_name
        || category?.category_id
        || i18next.t('ads:placementPlan.unknownCategory', {defaultValue: 'Unknown category'});
};

const getCompactQuantitySizeOptions = (sizeOptions, optimalSize, selectedSize) => {
    if (!sizeOptions.length || !optimalSize) {
        return sizeOptions.slice(0, 4);
    }
    const optimalIndex = sizeOptions.findIndex((size) => Number(size) === Number(optimalSize));
    if (optimalIndex < 0) {
        return sizeOptions.slice(0, 4);
    }
    const preferred = [];
    if (optimalIndex > 0) {
        preferred.push(sizeOptions[optimalIndex - 1]);
    }
    preferred.push(sizeOptions[optimalIndex]);
    if (optimalIndex + 1 < sizeOptions.length) {
        preferred.push(sizeOptions[optimalIndex + 1]);
    }
    if (optimalIndex + 2 < sizeOptions.length) {
        preferred.push(sizeOptions[optimalIndex + 2]);
    }
    const selected = Number(selectedSize || 0);
    if (selected && !preferred.includes(selected) && sizeOptions.includes(selected)) {
        preferred.push(selected);
    }
    return [...new Set(preferred)].sort((a, b) => a - b);
};

export const getReusablePackagePurchaseOptions = (category) => (
    getPlacementPurchaseOptions(category).filter((packet) => (
        packet.purchase_kind !== PLACEMENT_PURCHASE_SINGLE_AD && Number(packet.size || 0) !== 1
    ))
);

export const getPreferredReusablePlacementPacketForSize = (category, size, paymentMethod = '') => {
    const packets = sortPackets(getReusablePackagePurchaseOptions(category))
        .filter((packet) => Number(packet.size || 0) === Number(size))
        .filter((packet) => !paymentMethod || String(packet.payment_method || '') === String(paymentMethod));
    return packets.find(isStartPlacementPacket) || packets[0] || null;
};

export const getDefaultReusablePlacementPacket = (category) => {
    const packets = sortPackets(getReusablePackagePurchaseOptions(category));
    if (!packets.length) {
        return null;
    }
    const recommended = category?.recommended_package;
    if (recommended) {
        const recommendedKey = getPlacementPacketKey(category, recommended);
        const matchingRecommended = packets.find((packet) => getPlacementPacketKey(category, packet) === recommendedKey);
        if (matchingRecommended) {
            return matchingRecommended;
        }
    }
    const missingSlots = Number(category?.missing_slots || 0);
    const optimalSize = getQuantityThresholdSize(getPacketSizeOptions(packets), missingSlots);
    const preferredOptimal = optimalSize ? getPreferredReusablePlacementPacketForSize(category, optimalSize) : null;
    if (preferredOptimal) {
        return preferredOptimal;
    }
    const coveringPackets = packets.filter((packet) => Number(packet.size || 0) >= missingSlots);
    return coveringPackets.find(isStartPlacementPacket) || coveringPackets[0] || packets[0];
};

const getScheduledPackageVariantKey = (packet) => {
    if (packet?.purchase_kind === PLACEMENT_PURCHASE_MEGA_PACKET || String(packet?.packet_type || packet?.type || '').toLowerCase() === 'mega') {
        return 'mega';
    }
    return packet?.is_premium ? 'premium' : 'start';
};

const getReusablePlacementPacketsByVariant = (category, variant) => (
    sortPackets(getReusablePackagePurchaseOptions(category)).filter((packet) => getScheduledPackageVariantKey(packet) === variant)
);

const getDefaultReusablePlacementPacketForVariant = (category, variant) => {
    const packets = getReusablePlacementPacketsByVariant(category, variant);
    if (!packets.length) {
        return null;
    }
    const missingSlots = Number(category?.missing_slots || category?.selected_count || 0);
    const sizeOptions = getPacketSizeOptions(packets);
    const optimalSize = getQuantityThresholdSize(sizeOptions, missingSlots);
    const coveringPackets = optimalSize
        ? packets.filter((packet) => Number(packet.size || 0) === Number(optimalSize))
        : packets.filter((packet) => Number(packet.size || 0) >= missingSlots);
    return coveringPackets[0] || packets[0];
};

const getScheduledPackageVariantOptions = (category) => (
    ['start', 'premium', 'mega']
        .map((variant) => getDefaultReusablePlacementPacketForVariant(category, variant))
        .filter(Boolean)
);

const getSchedulablePlacementItems = (category) => (
    (category?.items || []).filter((item) => ![
        PLACEMENT_STATUS_API_ERROR,
        PLACEMENT_STATUS_INVALID_STATUS,
        PLACEMENT_STATUS_EXCLUDED,
    ].includes(item.status))
);

const getPackageOptionSize = (packet) => {
    const size = Number(packet?.size || 0);
    return Number.isFinite(size) ? size : 0;
};

const chooseMinimumSizePackageCombination = (options, requiredSlots) => {
    const required = Math.max(Number(requiredSlots || 1), 1);
    const candidates = (options || []).filter((packet) => getPackageOptionSize(packet) > 1);
    if (!candidates.length) {
        return [];
    }
    const maxSize = Math.max(...candidates.map(getPackageOptionSize));
    const limit = required + maxSize;
    const best = new Map([[0, {count: 0, combo: []}]]);
    for (let total = 1; total <= limit; total += 1) {
        let currentBest = null;
        candidates.forEach((packet) => {
            const size = getPackageOptionSize(packet);
            const previous = best.get(total - size);
            if (!previous) {
                return;
            }
            const candidate = {count: previous.count + 1, combo: [...previous.combo, packet]};
            if (!currentBest || candidate.count < currentBest.count) {
                currentBest = candidate;
            }
        });
        if (currentBest) {
            best.set(total, currentBest);
        }
    }
    const covering = [...best.entries()].filter(([total]) => total >= required);
    if (!covering.length) {
        return [];
    }
    covering.sort((a, b) => (
        (a[0] - required) - (b[0] - required)
        || a[1].count - b[1].count
    ));
    return covering[0][1].combo;
};

const chooseOptimalPackageCombination = (options, requiredSlots) => {
    const required = Math.max(Number(requiredSlots || 1), 1);
    const candidates = (options || []).filter((packet) => getPackageOptionSize(packet) > 1);
    if (!candidates.length) {
        return [];
    }
    const maxSize = Math.max(...candidates.map(getPackageOptionSize));
    const limit = required + maxSize;
    const best = new Map([[0, {amount: 0, count: 0, combo: []}]]);
    for (let total = 1; total <= limit; total += 1) {
        let currentBest = null;
        candidates.forEach((packet) => {
            const amount = Number(packet?.price?.amount);
            if (!Number.isFinite(amount)) {
                return;
            }
            const size = getPackageOptionSize(packet);
            const previous = best.get(total - size);
            if (!previous) {
                return;
            }
            const candidate = {
                amount: previous.amount + amount,
                count: previous.count + 1,
                combo: [...previous.combo, packet],
            };
            if (!currentBest || candidate.amount < currentBest.amount || (
                candidate.amount === currentBest.amount && candidate.count < currentBest.count
            )) {
                currentBest = candidate;
            }
        });
        if (currentBest) {
            best.set(total, currentBest);
        }
    }
    const covering = [...best.entries()].filter(([total]) => total >= required);
    if (covering.length) {
        covering.sort((a, b) => (
            a[1].amount - b[1].amount
            || (a[0] - required) - (b[0] - required)
            || a[1].count - b[1].count
        ));
        return covering[0][1].combo;
    }
    return chooseMinimumSizePackageCombination(candidates, required);
};

const summarizeScheduledPackageCombo = (combo) => {
    if (!combo?.length) {
        return {totalSlots: 0, totalPrice: null, hasUnknownPrice: false};
    }
    let totalSlots = 0;
    let totalAmount = 0;
    let currency = '';
    let hasUnknownPrice = false;
    (combo || []).forEach((packet) => {
        totalSlots += getPackageOptionSize(packet);
        const amount = Number(packet?.price?.amount);
        const packetCurrency = packet?.price?.currency || packet?.currency || '';
        if (!Number.isFinite(amount) || !packetCurrency) {
            hasUnknownPrice = true;
            return;
        }
        totalAmount += amount;
        if (!currency) {
            currency = packetCurrency;
        } else if (packetCurrency !== currency) {
            hasUnknownPrice = true;
        }
    });
    return {
        totalSlots,
        totalPrice: hasUnknownPrice ? null : {amount: totalAmount, currency},
        hasUnknownPrice,
    };
};

export const getScheduledPackageAutoSummary = (category, selectedPacket, options = {}) => {
    const selected = selectedPacket || getScheduledPackageVariantOptions(category)[0] || null;
    const rawRequiredSlots = options.requiredSlots;
    const requiredSlots = rawRequiredSlots === undefined || rawRequiredSlots === null
        ? getSchedulablePlacementItems(category).length
        : Math.max(Number(rawRequiredSlots || 0), 0);
    if (!selected || !requiredSlots) {
        return {requiredSlots, combo: [], totalSlots: 0, totalPrice: null, hasUnknownPrice: false};
    }
    const variant = getScheduledPackageVariantKey(selected);
    const paymentMethod = String(selected.payment_method || '');
    const candidates = getReusablePlacementPacketsByVariant(category, variant)
        .filter((packet) => !paymentMethod || String(packet.payment_method || '') === paymentMethod);
    const combo = chooseOptimalPackageCombination(candidates, requiredSlots);
    const summary = summarizeScheduledPackageCombo(combo);
    return {
        requiredSlots,
        combo,
        totalSlots: summary.totalSlots,
        totalPrice: summary.totalPrice,
        hasUnknownPrice: summary.hasUnknownPrice,
    };
};

const renderPackageSelector = (account, category, selectedPacket, categoryDisplayNames, options = {}) => {
    const rendered = renderReusablePackageSelector({
        account,
        category,
        selectedPacket,
        categoryDisplayNames,
        expandedPackageSizeKeysSet: options.expandedPackageSizeKeysSet,
    });
    const singleAdWarnings = `
        ${category.single_ad_purchase_unavailable ? `
            <div class="placement-plan-inline-warning">
                ${escapeHtml(i18next.t('ads:placementPlan.singleAdPriceUnavailable', {defaultValue: 'OLX did not return single-ad prices for this category.'}))}
            </div>
        ` : ''}
        ${category.single_ad_price_unavailable ? `
            <div class="placement-plan-inline-warning">
                ${escapeHtml(i18next.t('ads:placementPlan.singleAdPriceCalculatedByOlx', {defaultValue: 'OLX Partner API did not return a single-ad price. OLX will calculate it during purchase.'}))}
            </div>
        ` : ''}
    `;
    return `${rendered}${singleAdWarnings}`;
};

const renderScheduledPackageAutoSelector = (category, selectedPacket, renderOptions = {}) => {
    const categoryKey = getPlacementCategoryKey(category);
    const options = getScheduledPackageVariantOptions(category);
    if (!options.length) {
        return `<span class="placement-plan-muted">${i18next.t('ads:placementPlan.noAvailableReusablePackets', {defaultValue: 'No reusable packages are available. Use individual placement on adverts below.'})}</span>`;
    }
    const selectedVariant = getScheduledPackageVariantKey(selectedPacket || options[0]);
    const selected = options.find((packet) => getScheduledPackageVariantKey(packet) === selectedVariant) || selectedPacket || options[0];
    const autoSummary = getScheduledPackageAutoSummary(category, selected, {
        requiredSlots: renderOptions.scheduledPackageSelections?.[categoryKey]?.required_slots,
    });
    const groupedCombo = [];
    (autoSummary.combo || []).forEach((packet) => {
        const key = [
            getPacketVariantName(packet),
            getPackageOptionSize(packet),
            formatPlacementPacketPrice(packet),
            packet.id || '',
        ].join('|');
        const existing = groupedCombo.find((item) => item.key === key);
        if (existing) {
            existing.count += 1;
        } else {
            groupedCombo.push({key, packet, count: 1});
        }
    });
    const maxPrices = renderOptions.scheduledPackageMaxPrices || {};
    const explicitMax = maxPrices[categoryKey];
    const estimatedAmount = Number(autoSummary.totalPrice?.amount);
    const maxValue = explicitMax !== undefined && explicitMax !== null && explicitMax !== ''
        ? explicitMax
        : (Number.isFinite(estimatedAmount) ? estimatedAmount : '');
    const maxAmount = Number(maxValue);
    const isMaxBelowEstimate = autoSummary.totalPrice
        && Number.isFinite(maxAmount)
        && maxAmount < estimatedAmount;
    return `
        <div class="placement-plan-scheduled-package-auto">
            <div class="placement-plan-selector-label">${escapeHtml(i18next.t('ads:placementPlan.packageVariantAtLaunch', {defaultValue: 'Package type at launch'}))}</div>
            <div class="placement-plan-package-options placement-plan-package-options--compact">
                ${options.map((packet) => {
                    const variant = getScheduledPackageVariantKey(packet);
                    const isSelected = variant === selectedVariant;
                    return `
                        <button
                            type="button"
                            class="placement-plan-package-card placement-plan-scheduled-package-variant ${isSelected ? 'active' : ''}"
                            data-placement-category-key="${escapeHtml(categoryKey)}"
                            data-placement-packet-key="${escapeHtml(getPlacementPacketKey(category, packet))}"
                        >
                            <span>${escapeHtml(getPacketVariantName(packet))}</span>
                            <strong>${escapeHtml(formatPlacementPacketPrice(packet))}</strong>
                            <em>${escapeHtml(i18next.t('ads:placementPlan.autoPackageSizeHint', {defaultValue: 'Minimum size will be selected at launch.'}))}</em>
                            <small>${escapeHtml(isSelected
                                ? i18next.t('ads:placementPlan.selectedPackage', {defaultValue: 'Selected'})
                                : i18next.t('ads:placementPlan.selectPackage', {defaultValue: 'Select'}))}</small>
                        </button>
                    `;
                }).join('')}
            </div>
            <div class="placement-plan-scheduled-auto-forecast">
                <div class="placement-plan-scheduled-auto-heading">
                    <strong>${escapeHtml(i18next.t('ads:placementPlan.scheduledAutoPurchaseTitle', {defaultValue: 'Will be bought at launch'}))}</strong>
                    <span>${escapeHtml(i18next.t('ads:placementPlan.scheduledAutoRequiredSlots', {defaultValue: '{{count}} adverts selected', count: autoSummary.requiredSlots}))}</span>
                </div>
                ${groupedCombo.length ? `
                    <div class="placement-plan-scheduled-auto-combo">
                        ${groupedCombo.map(({packet, count}) => `
                            <div class="placement-plan-scheduled-auto-row">
                                <span>${escapeHtml(getPacketVariantName(packet))} ${escapeHtml(getPackageOptionSize(packet))}</span>
                                <strong>x${escapeHtml(count)}</strong>
                                <em>${escapeHtml(formatPlacementPacketPrice(packet))}</em>
                            </div>
                        `).join('')}
                    </div>
                ` : `
                    <span class="placement-plan-muted">${escapeHtml(i18next.t('ads:placementPlan.scheduledAutoNoOptions', {defaultValue: 'No matching package combination is available.'}))}</span>
                `}
                <div class="placement-plan-scheduled-auto-total">
                    <span>${escapeHtml(i18next.t('ads:placementPlan.scheduledAutoCurrentEstimate', {defaultValue: 'Estimate now:'}))}</span>
                    <strong>${escapeHtml(autoSummary.totalPrice
                        ? formatMoney(autoSummary.totalPrice)
                        : i18next.t('ads:placementPlan.scheduledAutoUnknownPrice', {defaultValue: 'OLX will calculate at launch'}))}</strong>
                </div>
                ${autoSummary.totalPrice ? `
                    <label class="placement-plan-scheduled-auto-max ${isMaxBelowEstimate ? 'has-error' : ''}">
                        <span>${escapeHtml(i18next.t('ads:placementPlan.scheduledAutoMaxAtLaunch', {defaultValue: 'Maximum at launch'}))}</span>
                        <input
                            type="number"
                            step="0.01"
                            min="${escapeHtml(estimatedAmount)}"
                            class="og-input placement-plan-scheduled-package-max-input"
                            data-placement-category-key="${escapeHtml(categoryKey)}"
                            data-placement-scheduled-estimate="${escapeHtml(estimatedAmount)}"
                            data-placement-scheduled-currency="${escapeHtml(autoSummary.totalPrice.currency || '')}"
                            value="${escapeHtml(maxValue)}"
                        >
                        ${isMaxBelowEstimate ? `
                            <em>${escapeHtml(i18next.t('ads:placementPlan.scheduledAutoMaxBelowEstimate', {defaultValue: 'Maximum cannot be lower than the current estimate.'}))}</em>
                        ` : ''}
                    </label>
                ` : `
                    <p class="placement-plan-scheduled-auto-note">
                        ${escapeHtml(i18next.t('ads:placementPlan.scheduledAutoUnknownPolicy', {defaultValue: 'Price is unknown, so the scheduled purchase will run best-effort only because this strategy is explicit.'}))}
                    </p>
                `}
                <p class="placement-plan-scheduled-auto-note">
                    ${escapeHtml(i18next.t('ads:placementPlan.scheduledAutoCopy', {defaultValue: 'Current free slots are not included in this forecast because they may change by launch. If slots are available then, the server will buy less or skip package purchase.'}))}
                </p>
            </div>
        </div>
    `;
};

const renderScheduledCategoryStrategy = (account, category, selectedPacket, categoryDisplayNames, options = {}) => {
    if (!options.isScheduled || isPaidFeatureResourceKind(category.resource_kind)) {
        return '';
    }
    const categoryKey = getPlacementCategoryKey(category);
    const isDraftPublish = options.operationType === PLACEMENT_OPERATION_PUBLISH_DRAFTS;
    if (
        options.operationType === PLACEMENT_OPERATION_ACTIVATE_ADS
        && !(category.items || []).some((item) => item.created_by_service === true)
    ) {
        return '';
    }
    const strategy = getScheduledCategoryStrategy(categoryKey, options);
    const packageMode = getScheduledPackageSelectionMode(categoryKey, options);
    const strategyOptions = [
        ...(isDraftPublish ? [{
            value: SCHEDULED_PLACEMENT_STRATEGY_UNPAID,
            label: i18next.t('ads:placementPlan.createWithoutPayment', {defaultValue: 'Create without purchase'}),
            description: i18next.t('ads:placementPlan.unpaidStrategyDescription', {defaultValue: 'The service will not buy a package; OLX may use available slots or create unpaid/limited/new adverts.'}),
        }] : []),
        {
            value: SCHEDULED_PLACEMENT_STRATEGY_USE_AVAILABLE,
            label: i18next.t('ads:placementPlan.useSlotsAtLaunch', {defaultValue: 'Use slots at launch'}),
            description: i18next.t('ads:placementPlan.useSlotsAtLaunchDescription', {defaultValue: 'Do not buy anything; run only if slots are available at launch.'}),
        },
        {
            value: SCHEDULED_PLACEMENT_STRATEGY_PACKAGE,
            label: i18next.t('ads:placementPlan.buyPackageAtLaunch', {defaultValue: 'Buy package at launch'}),
            description: i18next.t('ads:placementPlan.buyPackageAtLaunchDescription', {defaultValue: 'Buy a category package at launch only if current slots are missing.'}),
        },
        {
            value: SCHEDULED_CATEGORY_STRATEGY_MANUAL,
            label: i18next.t('ads:placementPlan.manualStrategy', {defaultValue: 'Manual setup'}),
            description: i18next.t('ads:placementPlan.manualStrategyDescription', {defaultValue: 'Choose a separate action for individual adverts below.'}),
        },
    ];
    return `
        <section class="placement-plan-scheduled-strategy" data-placement-category-key="${escapeHtml(categoryKey)}">
            <div class="placement-plan-scheduled-strategy-header">
                <div>
                    <strong>${escapeHtml(i18next.t('ads:placementPlan.launchStrategy', {defaultValue: 'Launch strategy'}))}</strong>
                    <span>${escapeHtml(i18next.t('ads:placementPlan.launchStrategyHint', {defaultValue: 'This decision is applied when the scheduled operation runs.'}))}</span>
                </div>
            </div>
            <div class="placement-plan-scheduled-strategy-options">
                ${strategyOptions.map((item) => `
                    <button
                        type="button"
                        class="placement-plan-scheduled-strategy-option ${strategy === item.value ? 'active' : ''}"
                        data-placement-category-key="${escapeHtml(categoryKey)}"
                        data-placement-category-strategy="${escapeHtml(item.value)}"
                    >
                        <span>${escapeHtml(item.label)}</span>
                        <em>${escapeHtml(item.description)}</em>
                    </button>
                `).join('')}
            </div>
            ${strategy === SCHEDULED_PLACEMENT_STRATEGY_PACKAGE ? `
                <div class="placement-plan-scheduled-package-panel">
                    <div class="placement-plan-scheduled-package-mode">
                        <button
                            type="button"
                            class="placement-plan-scheduled-package-mode-option ${packageMode === SCHEDULED_PACKAGE_SELECTION_AUTO ? 'active' : ''}"
                            data-placement-category-key="${escapeHtml(categoryKey)}"
                            data-placement-package-selection-mode="${SCHEDULED_PACKAGE_SELECTION_AUTO}"
                        >
                            ${escapeHtml(i18next.t('ads:placementPlan.autoPackageMode', {defaultValue: 'Auto'}))}
                        </button>
                        <button
                            type="button"
                            class="placement-plan-scheduled-package-mode-option ${packageMode === SCHEDULED_PACKAGE_SELECTION_FIXED ? 'active' : ''}"
                            data-placement-category-key="${escapeHtml(categoryKey)}"
                            data-placement-package-selection-mode="${SCHEDULED_PACKAGE_SELECTION_FIXED}"
                        >
                            ${escapeHtml(i18next.t('ads:placementPlan.fixedPackageMode', {defaultValue: 'Fixed package'}))}
                        </button>
                    </div>
                    <p class="placement-plan-scheduled-package-copy">
                        ${escapeHtml(packageMode === SCHEDULED_PACKAGE_SELECTION_AUTO
                            ? i18next.t('ads:placementPlan.autoPackageModeHint', {defaultValue: 'At launch the server will buy the minimum matching package only if slots are still missing.'})
                            : i18next.t('ads:placementPlan.fixedPackageModeHint', {defaultValue: 'At launch the server will buy the selected package only if slots are still missing.'}))}
                    </p>
                    ${packageMode === SCHEDULED_PACKAGE_SELECTION_FIXED
                        ? renderPackageSelector(account, category, selectedPacket, categoryDisplayNames, options)
                        : renderScheduledPackageAutoSelector(category, selectedPacket, options)}
                    <p class="placement-plan-resource-action-note">
                        ${escapeHtml(packageMode === SCHEDULED_PACKAGE_SELECTION_AUTO
                            ? i18next.t('ads:placementPlan.scheduledAutoPriceGuard', {defaultValue: 'At launch the server will buy packages only if the total price stays within the selected maximum.'})
                            : selectedPacket?.price_unavailable
                            ? i18next.t('ads:placementPlan.olxCalculatesAtLaunch', {defaultValue: 'OLX will calculate the final price at launch.'})
                            : i18next.t('ads:placementPlan.scheduledPackagePriceGuard', {defaultValue: 'If the package price increases before launch, the server will not buy it.'}))}
                    </p>
                </div>
            ` : ''}
        </section>
    `;
};

const renderPaidFeatureResources = (account, category, isResourceOpen) => {
    const categoryKey = getPlacementCategoryKey(category);
    const feature = category.recommended_feature || null;
    const paymentMethods = (account.payment_methods || []).map((method) => method.label || method.value);
    return `
        <details class="placement-plan-resource-block" data-placement-resource-key="${escapeHtml(categoryKey)}" ${isResourceOpen ? 'open' : ''}>
            <summary class="placement-plan-resource-summary">
                <div>
                    <strong>${i18next.t('ads:paidFeatures.resourceTitle', {defaultValue: 'Paid feature'})}</strong>
                    <span class="placement-plan-muted">
                        ${feature
                            ? escapeHtml(`${feature.name || feature.code} · ${formatPaidFeaturePrice(feature)}`)
                            : escapeHtml(i18next.t('ads:paidFeatures.noAvailableFeature', {defaultValue: 'No OLX bump feature is available.'}))}
                    </span>
                </div>
                <span class="placement-plan-resource-toggle">
                    <span>${isResourceOpen
                        ? i18next.t('ads:placementPlan.collapseResources', {defaultValue: 'Collapse'})
                        : i18next.t('ads:placementPlan.expandResources', {defaultValue: 'Expand'})}</span>
                    ${renderPlacementChevronIcon()}
                </span>
            </summary>
            <div class="placement-plan-resource-grid">
                <div>
                    <span>${i18next.t('ads:paidFeatures.featureCode', {defaultValue: 'Feature code'})}</span>
                    <strong>${escapeHtml(feature?.code || '-')}</strong>
                </div>
                <div>
                    <span>${i18next.t('ads:paidFeatures.duration', {defaultValue: 'Duration'})}</span>
                    <strong>${escapeHtml(feature?.duration ? i18next.t('ads:paidFeatures.featureDuration', {defaultValue: '{{duration}} days', duration: feature.duration}) : '-')}</strong>
                </div>
                <div>
                    <span>${i18next.t('ads:placementPlan.balance', {defaultValue: 'OLX balance'})}</span>
                    <strong>${escapeHtml(formatMoney(account.olx_balance))}</strong>
                </div>
                <div>
                    <span>${i18next.t('ads:placementPlan.paymentMethods', {defaultValue: 'Payment methods'})}</span>
                    ${renderCompactList(paymentMethods, i18next.t('ads:placementPlan.noPaymentMethods', {defaultValue: 'No payment methods'}))}
                </div>
            </div>
            ${renderPaidFeatureSelector(category)}
        </details>
    `;
};

const renderAdvertisingFeatureResources = (account, category, isResourceOpen) => {
    const categoryKey = getPlacementCategoryKey(category);
    const options = category.advertising_options || category.paid_feature_options || [];
    const paymentMethods = (account.payment_methods || []).map((method) => method.label || method.value);
    return `
        <details class="placement-plan-resource-block" data-placement-resource-key="${escapeHtml(categoryKey)}" ${isResourceOpen ? 'open' : ''}>
            <summary class="placement-plan-resource-summary">
                <div>
                    <strong>${i18next.t('ads:paidFeatures.advertisingTitle', {defaultValue: 'OLX advertising'})}</strong>
                    <span class="placement-plan-muted">
                        ${options.length
                            ? escapeHtml(options.map(getAdvertisingOptionLabel).join(' · '))
                            : escapeHtml(category.purchase_error || i18next.t('ads:paidFeatures.noAdvertisingFeature', {defaultValue: 'No supported OLX advertising option is available.'}))}
                    </span>
                </div>
                <span class="placement-plan-resource-toggle">
                    <span>${isResourceOpen
                        ? i18next.t('ads:placementPlan.collapseResources', {defaultValue: 'Collapse'})
                        : i18next.t('ads:placementPlan.expandResources', {defaultValue: 'Expand'})}</span>
                    ${renderPlacementChevronIcon()}
                </span>
            </summary>
            <div class="placement-plan-resource-grid">
                <div>
                    <span>${i18next.t('ads:paidFeatures.availableOptions', {defaultValue: 'Options'})}</span>
                    <strong>${escapeHtml(options.map(getAdvertisingOptionLabel).join(' · ') || '-')}</strong>
                </div>
                <div>
                    <span>${i18next.t('ads:placementPlan.balance', {defaultValue: 'OLX balance'})}</span>
                    <strong>${escapeHtml(formatMoney(account.olx_balance))}</strong>
                </div>
                <div>
                    <span>${i18next.t('ads:placementPlan.paymentMethods', {defaultValue: 'Payment methods'})}</span>
                    ${renderCompactList(paymentMethods, i18next.t('ads:placementPlan.noPaymentMethods', {defaultValue: 'No payment methods'}))}
                </div>
            </div>
            ${category.price_unknown ? `
                <div class="placement-plan-warning">
                    ${escapeHtml(i18next.t('ads:paidFeatures.unknownPriceWarning', {
                        defaultValue: 'OLX did not return a pre-purchase price. The final price will be calculated by OLX when the operation runs.',
                    }))}
                </div>
            ` : ''}
        </details>
    `;
};

const renderCategoryResources = (account, category, allowExclude, selectedPacket, isResourceOpen, categoryDisplayNames, options = {}) => {
    if (category.resource_kind === PLACEMENT_RESOURCE_ADVERTISING_FEATURE) {
        return renderAdvertisingFeatureResources(account, category, isResourceOpen);
    }
    if (isPaidFeatureResourceKind(category.resource_kind)) {
        return renderPaidFeatureResources(account, category, isResourceOpen);
    }
    const isScheduled = Boolean(options.isScheduled);
    const missingSlots = Number(category.missing_slots || 0);
    const categoryKey = getPlacementCategoryKey(category);
    const resourcePreviewPending = Boolean(options.resourcePreviewPending);
    if (resourcePreviewPending) {
        return `
            <div class="placement-plan-resource-block placement-plan-resource-block--pending" data-placement-resource-key="${escapeHtml(categoryKey)}" aria-busy="true">
                <div class="placement-plan-resource-summary">
                    <div>
                        <strong>${escapeHtml(i18next.t('ads:placementPlan.checkingResources', {defaultValue: 'Checking OLX placements...'}))}</strong>
                        <span class="placement-plan-muted">${escapeHtml(i18next.t('ads:placementOperation.loadingOlxData', {defaultValue: 'Loading OLX data...'}))}</span>
                    </div>
                    <span class="placement-operation-spinner" aria-hidden="true"></span>
                </div>
                <div class="placement-plan-resource-skeleton-grid" aria-hidden="true">
                    <span class="placement-plan-skeleton-line"></span>
                    <span class="placement-plan-skeleton-line"></span>
                    <span class="placement-plan-skeleton-line"></span>
                    <span class="placement-plan-skeleton-line"></span>
                </div>
            </div>
        `;
    }
    const boughtPackets = (category.bought_packets || []).map((packet) => {
        const left = packet.left !== null && packet.left !== undefined
            ? i18next.t('ads:placementPlan.packetLeft', {defaultValue: '{{left}} left', left: packet.left})
            : '';
        return [renderPacketLabel(packet), left].filter(Boolean).join(' · ');
    });
    const paymentMethods = (account.payment_methods || []).map((method) => method.label || method.value);
    const resourceItems = options.visibleItems || category.items || [];
    const problematicItemKeys = resourceItems
        .filter((item) => [PLACEMENT_STATUS_NEEDS_PACKAGE, PLACEMENT_STATUS_INVALID_STATUS, PLACEMENT_STATUS_API_ERROR].includes(item.status))
        .map((item) => item.item_key);
    const selected = selectedPacket || getDefaultReusablePlacementPacket(category);
    return `
        <details class="placement-plan-resource-block ${options.purchasingCategoryKey === categoryKey ? 'is-loading' : ''}" data-placement-resource-key="${escapeHtml(categoryKey)}" ${isResourceOpen ? 'open' : ''}>
            <summary class="placement-plan-resource-summary">
                <div>
                    <strong>${i18next.t(isScheduled ? 'ads:placementPlan.currentAvailability' : 'ads:placementPlan.resourcesAndPackages', {defaultValue: isScheduled ? 'Current availability' : 'Resources and packages'})}</strong>
                    <span class="placement-plan-muted">
                        ${escapeHtml(i18next.t(isScheduled ? 'ads:placementPlan.currentResourceSummary' : 'ads:placementPlan.resourceSummary', {
                            defaultValue: isScheduled ? 'Available now: {{available}}, missing now: {{missing}}' : 'Available: {{available}}, missing: {{missing}}, selected: {{selected}}',
                            available: category.available_slots === null || category.available_slots === undefined ? '-' : Number(category.available_slots),
                            missing: missingSlots,
                            selected: selected ? `${selected.size} / ${formatPlacementPacketPrice(selected)}` : '-',
                        }))}
                    </span>
                </div>
                <span class="placement-plan-resource-toggle">
                    <span>${isResourceOpen
                        ? i18next.t('ads:placementPlan.collapseResources', {defaultValue: 'Collapse'})
                        : i18next.t('ads:placementPlan.expandResources', {defaultValue: 'Expand'})}</span>
                    ${renderPlacementChevronIcon()}
                </span>
            </summary>
            <div class="placement-plan-resource-grid">
                <div>
                    <span>${i18next.t(isScheduled ? 'ads:placementPlan.availableNow' : 'ads:placementPlan.availableSlots', {defaultValue: isScheduled ? 'Available now' : 'Available slots'})}</span>
                    <strong>${category.available_slots === null || category.available_slots === undefined ? '-' : Number(category.available_slots)}</strong>
                </div>
                <div>
                    <span>${i18next.t(isScheduled ? 'ads:placementPlan.missingNow' : 'ads:placementPlan.missingSlots', {defaultValue: isScheduled ? 'Missing now' : 'Missing slots'})}</span>
                    <strong>${missingSlots}</strong>
                </div>
                <div>
                    <span>${i18next.t('ads:placementPlan.balance', {defaultValue: 'OLX balance'})}</span>
                    <strong>${escapeHtml(formatMoney(account.olx_balance))}</strong>
                </div>
                <div>
                    <span>${i18next.t('ads:placementPlan.paymentMethods', {defaultValue: 'Payment methods'})}</span>
                    ${renderCompactList(paymentMethods, i18next.t('ads:placementPlan.noPaymentMethods', {defaultValue: 'No payment methods'}))}
                </div>
            </div>
            <div class="placement-plan-resource-row">
                <span>${i18next.t('ads:placementPlan.boughtPackets', {defaultValue: 'Bought packets'})}</span>
                ${renderCompactList(boughtPackets, i18next.t('ads:placementPlan.noBoughtPackets', {defaultValue: 'No bought packets'}))}
            </div>
            ${(!isScheduled && missingSlots > 0) ? `
                ${options.purchasingCategoryKey === categoryKey ? `
                    <div class="placement-plan-resource-overlay">
                        <span class="placement-operation-spinner"></span>
                        <span>${i18next.t('ads:placementOperation.purchasingPlacement', {defaultValue: 'Purchasing placement in OLX...'})}</span>
                    </div>
                ` : ''}
                ${renderPackageSelector(account, category, selected, categoryDisplayNames, options)}
                ${category.purchase_error ? `<div class="placement-plan-warning">${escapeHtml(category.purchase_error)}</div>` : ''}
            ` : ''}
            ${(allowExclude && problematicItemKeys.length) || (!isScheduled && missingSlots > 0 && selected) ? `
                <div class="placement-plan-resource-actions">
                    ${allowExclude && problematicItemKeys.length ? `
                        <button
                            type="button"
                            class="outline-button placement-plan-exclude-category-problems"
                            data-placement-item-keys="${escapeHtml(problematicItemKeys.join('|'))}"
                        >
                            ${i18next.t('ads:placementPlan.excludeProblematicCategory', {
                                count: problematicItemKeys.length,
                                defaultValue: 'Exclude {{count}}',
                            })}
                        </button>
                    ` : ''}
                    ${!isScheduled && missingSlots > 0 && selected ? `
                        ${selected.requires_advert_creation ? `
                            <span class="placement-plan-resource-action-note">${i18next.t('ads:placementPlan.singleAdAfterCreation', {defaultValue: 'This placement is bought after OLX creates the advert.'})}</span>
                        ` : `
                            <button
                                type="button"
                                class="filled-button placement-plan-purchase-package"
                                data-placement-category-key="${escapeHtml(categoryKey)}"
                                ${options.purchaseRefreshRequired ? 'disabled' : ''}
                            >
                                ${i18next.t('ads:placementPlan.buyPackage', {defaultValue: 'Buy package now'})}
                            </button>
                        `}
                    ` : ''}
                </div>
            ` : ''}
        </details>
    `;
};

const renderCategory = (account, category, activeFilter, allowExclude, options) => {
    const categoryItems = category.items || [];
    const visibleItems = categoryItems.filter((item) => placementItemMatchesFilter(item, activeFilter));
    if (!visibleItems.length) {
        return '';
    }
    const categoryKey = getPlacementCategoryKey(category);
    const isResourceOpen = Array.isArray(options.expandedResourceKeys)
        ? options.expandedResourceKeys.includes(categoryKey)
        : Number(category.missing_slots || 0) > 0;
    const selectedPacket = getReusablePackagePurchaseOptions(category).find((packet) => (
        getPlacementPacketKey(category, packet) === options.packageSelections[categoryKey]
    )) || getDefaultReusablePlacementPacket(category);
    const hasSinglePlacementOptions = (category.purchase_options?.single_ad_options || []).length > 0;
    const hasEstimatedSinglePlacementPrice = !category.single_ad_price_unavailable
        && (category.purchase_options?.single_ad_options || []).some((option) => option.price_estimated);
    const hasPaymentRequiredItems = visibleItems.some((item) => item.status === PLACEMENT_STATUS_NEEDS_PACKAGE);
    const isScheduled = Boolean(options.isScheduled);
    const resourcePreviewPending = Boolean(options.resourcePreviewPending);
    const scheduledCategoryStrategy = getScheduledCategoryStrategy(categoryKey, options);
    const hasResourceItems = resourcePreviewPending || isPaidFeatureResourceKind(category.resource_kind) || hasPaymentRequiredItems || isScheduled;
    const itemCount = visibleItems.length;
    const itemManagementLabel = activeFilter === 'needs_package' && hasPaymentRequiredItems
        ? i18next.t('ads:placementPlan.paymentRequiredAdverts', {defaultValue: 'Require payment ({{count}})', count: itemCount})
        : i18next.t('ads:placementPlan.manageAdverts', {defaultValue: 'Manage adverts ({{count}})', count: itemCount});
    const areItemsOpen = Array.isArray(options.expandedCategoryKeys)
        ? options.expandedCategoryKeys.includes(categoryKey)
        : hasSinglePlacementOptions;
    const categoryDisplayName = getCategoryDisplayName(category, options.categoryDisplayNames);
    const missingSlots = Number(category.missing_slots || 0);
    const categoryStatsText = resourcePreviewPending
        ? i18next.t('ads:placementPlan.checkingResources', {defaultValue: 'Checking OLX placements...'})
        : isPaidFeatureResourceKind(category.resource_kind)
        ? i18next.t('ads:paidFeatures.selectedReadyErrors', {
            defaultValue: 'Selected: {{selected}}, ready: {{ready}}, unavailable: {{errors}}',
            selected: category.selected_count || 0,
            ready: category.ready_count || 0,
            errors: category.error_count || 0,
        })
        : i18next.t('ads:placementPlan.selectedReadyMissing', {
            defaultValue: 'Selected: {{selected}}, ready: {{ready}}, missing: {{missing}}',
            selected: category.selected_count || 0,
            ready: category.ready_count || 0,
            missing: missingSlots,
        });
    const categorySummary = categoryStatsText + renderEstimatedCostSuffix(category.estimated_total_cost, resourcePreviewPending);
    const hasErrors = Number(category.error_count || 0) > 0 || category.status === PLACEMENT_STATUS_API_ERROR || category.status === PLACEMENT_STATUS_INVALID_STATUS;
    const statusClass = resourcePreviewPending && !hasErrors ? PLACEMENT_STATUS_CHECKING : hasErrors ? 'error' : category.status;
    const statusLabel = resourcePreviewPending && !hasErrors
        ? placementStatusLabel(PLACEMENT_STATUS_CHECKING)
        : placementStatusLabel(category.status);
    const collapsed = options.collapsedPreflightCategoryKeysSet?.has(categoryKey);
    const toggleLabel = collapsed
        ? i18next.t('ads:placementSchedule.expandGroup', {defaultValue: 'Expand'})
        : i18next.t('ads:placementSchedule.collapseGroup', {defaultValue: 'Collapse'});
    return `
        <section class="placement-plan-category placement-plan-category--preflight" data-placement-category-key="${escapeHtml(categoryKey)}">
            <header class="placement-plan-category-header placement-plan-preflight-group-header">
                <div>
                    <strong>${escapeHtml(categoryDisplayName)}</strong>
                    <span class="placement-plan-muted">
                        ${categorySummary}
                    </span>
                    ${placementWarningLabel(category) ? `<span class="placement-plan-warning">${escapeHtml(placementWarningLabel(category))}</span>` : ''}
                </div>
                <div class="placement-plan-category-tools">
                    <span class="placement-plan-status placement-plan-status--${escapeHtml(statusClass)}">${escapeHtml(statusLabel)}</span>
                    <button
                        type="button"
                        class="placement-plan-collapse-toggle placement-plan-toggle-category ${collapsed ? '' : 'is-expanded'}"
                        data-placement-category-key="${escapeHtml(categoryKey)}"
                        aria-expanded="${collapsed ? 'false' : 'true'}"
                        aria-label="${escapeHtml(toggleLabel)}"
                        title="${escapeHtml(toggleLabel)}"
                    >
                        ${renderPlacementChevronIcon()}
                    </button>
                </div>
            </header>
            ${collapsed ? '' : `
                ${renderScheduledCategoryStrategy(account, category, selectedPacket, options.categoryDisplayNames, {
                    ...options,
                    isScheduled,
                })}
                ${hasResourceItems ? renderCategoryResources(account, category, allowExclude, selectedPacket, isResourceOpen, options.categoryDisplayNames, {
                    ...options,
                    visibleItems,
                }) : ''}
                <details class="placement-plan-item-management" data-placement-items-key="${escapeHtml(categoryKey)}" ${areItemsOpen ? 'open' : ''}>
                    <summary>
                        <span>${escapeHtml(itemManagementLabel)}</span>
                        <span
                            class="placement-plan-collapse-toggle placement-plan-toggle-items ${areItemsOpen ? 'is-expanded' : ''}"
                            data-placement-items-key="${escapeHtml(categoryKey)}"
                            aria-expanded="${areItemsOpen ? 'true' : 'false'}"
                            aria-hidden="true"
                        >
                            ${renderPlacementChevronIcon()}
                        </span>
                    </summary>
                    ${hasPaymentRequiredItems && hasEstimatedSinglePlacementPrice ? `
                        <div class="placement-plan-inline-warning">
                            ${escapeHtml(i18next.t('ads:placementPlan.singleAdPriceEstimated', {defaultValue: 'ℹ Estimated cost'}))}
                        </div>
                    ` : ''}
                    ${renderMasterActions(category, visibleItems, {
                        allowExclude,
                        operationType: options.operationType,
                        singlePlacementSelections: options.singlePlacementSelections || {},
                        scheduledItemStrategies: options.scheduledItemStrategies || {},
                        unpaidDraftItemKeysSet: options.unpaidDraftItemKeysSet || new Set(),
                        actionSelectOpenKey: options.actionSelectOpenKey || '',
                        isScheduled,
                        resourcePreviewPending,
                        scheduledCategoryStrategies: options.scheduledCategoryStrategies || {},
                        scheduledPackageSelection: options.scheduledPackageSelections?.[categoryKey] || null,
                        advertisingItemSelections: options.advertisingItemSelections || {},
                    })}
                    <div class="placement-plan-items">
                        ${visibleItems.map((item) => renderItem(item, allowExclude, {
                            categoryKey,
                            resourceKind: category.resource_kind,
                            operationType: options.operationType,
                            singlePlacementOptions: category.purchase_options?.single_ad_options || [],
                            singlePlacementSelections: options.singlePlacementSelections || {},
                            advertisingItemSelections: options.advertisingItemSelections || {},
                            scheduledItemStrategies: options.scheduledItemStrategies || {},
                            unpaidDraftItemKeysSet: options.unpaidDraftItemKeysSet || new Set(),
                            actionSelectOpenKey: options.actionSelectOpenKey || '',
                            isScheduled,
                            resourcePreviewPending,
                            scheduledCategoryStrategy,
                            scheduledPackageSelection: options.scheduledPackageSelections?.[categoryKey] || null,
                        })).join('')}
                    </div>
                </details>
            `}
        </section>
    `;
};

const renderScheduleCategory = (account, category, options) => {
    const categoryDisplayName = getCategoryDisplayName(category, options.categoryDisplayNames);
    const allScheduleItems = (category.items || []).filter((item) => item.status !== PLACEMENT_STATUS_EXCLUDED);
    const scheduleItems = allScheduleItems.filter((item) => (
        !options.scheduleVisibleKeysSet || options.scheduleVisibleKeysSet.has(item.item_key)
    ));
    if (!scheduleItems.length) {
        return '';
    }
    const categoryKey = getPlacementCategoryKey(category);
    const collapsed = options.scheduleCollapsedCategoryKeysSet?.has(categoryKey);
    const stats = getScheduleSelectionStats(scheduleItems, options);
    const toggleLabel = collapsed
        ? i18next.t('ads:placementSchedule.expandGroup', {defaultValue: 'Expand'})
        : i18next.t('ads:placementSchedule.collapseGroup', {defaultValue: 'Collapse'});
    return `
        <section class="placement-plan-category placement-plan-category--schedule" data-schedule-category-key="${escapeHtml(categoryKey)}">
            <header class="placement-plan-category-header placement-schedule-group-header">
                <label class="placement-schedule-group-check">
                    <span class="placement-schedule-check-slot">
                        <input
                            type="checkbox"
                            class="form-check-input ogo-checkbox placement-schedule-category-check"
                            data-schedule-category-key="${escapeHtml(categoryKey)}"
                        >
                    </span>
                    <span class="placement-schedule-check-text">
                        <strong>${escapeHtml(categoryDisplayName)}</strong>
                        <span class="placement-plan-muted">
                            ${i18next.t('ads:placementSchedule.groupSelectionCount', {
                                defaultValue: 'Selected: {{selected}} of {{total}}',
                                selected: stats.selected,
                                total: stats.total,
                            })}
                            ${allScheduleItems.length !== scheduleItems.length ? ` · ${i18next.t('ads:placementSchedule.visibleCount', {
                                defaultValue: '{{count}} visible',
                                count: scheduleItems.length,
                            })}` : ''}
                        </span>
                    </span>
                </label>
                <button
                    type="button"
                    class="placement-schedule-collapse-toggle placement-schedule-toggle-category ${collapsed ? '' : 'is-expanded'}"
                    data-schedule-category-key="${escapeHtml(categoryKey)}"
                    aria-expanded="${collapsed ? 'false' : 'true'}"
                    aria-label="${escapeHtml(toggleLabel)}"
                    title="${escapeHtml(toggleLabel)}"
                >
                    ${renderPlacementChevronIcon()}
                </button>
            </header>
            ${collapsed ? '' : `
                <div class="placement-plan-items placement-plan-items--schedule">
                    ${scheduleItems.map((item) => renderScheduleItem(item, options)).join('')}
                </div>
            `}
        </section>
    `;
};

const renderAccount = (account, activeFilter, allowExclude, options) => {
    if (isAdvertisingAccount(account)) {
        return renderAdvertisingAccount(account, activeFilter, allowExclude, options);
    }
    const accountKey = String(account.account_id || '');
    const categories = (account.categories || [])
        .map((category) => renderCategory(account, category, activeFilter, allowExclude, options))
        .filter(Boolean)
        .join('');
    if (!categories) {
        return '';
    }
    const collapsed = options.collapsedPreflightAccountKeysSet?.has(accountKey);
    const resourcePreviewPending = Boolean(options.resourcePreviewPending);
    const toggleLabel = collapsed
        ? i18next.t('ads:placementSchedule.expandGroup', {defaultValue: 'Expand'})
        : i18next.t('ads:placementSchedule.collapseGroup', {defaultValue: 'Collapse'});
    return `
        <section class="placement-plan-account placement-plan-account--preflight" data-placement-account-key="${escapeHtml(accountKey)}">
            <header class="placement-plan-preflight-group-header">
                <div>
                    <strong>${escapeHtml(account.account_name || account.account_id)}</strong>
                    <span class="placement-plan-muted">
                        ${resourcePreviewPending ? i18next.t('ads:placementPlan.checkingResources', {
                            defaultValue: 'Checking OLX placements...',
                        }) : i18next.t('ads:placementPlan.accountStats', {
                            defaultValue: 'Selected: {{selected}}, ready: {{ready}}, problems: {{problems}}',
                            selected: account.total_selected || 0,
                            ready: account.total_ready || 0,
                            problems: Number(account.total_needs_package || 0) + Number(account.total_errors || 0),
                        }) + renderEstimatedCostSuffix(account.total_estimated_cost, resourcePreviewPending)}
                    </span>
                </div>
                <button
                    type="button"
                    class="placement-plan-collapse-toggle placement-plan-toggle-account ${collapsed ? '' : 'is-expanded'}"
                    data-placement-account-key="${escapeHtml(accountKey)}"
                    aria-expanded="${collapsed ? 'false' : 'true'}"
                    aria-label="${escapeHtml(toggleLabel)}"
                    title="${escapeHtml(toggleLabel)}"
                >
                    ${renderPlacementChevronIcon()}
                </button>
            </header>
            ${collapsed ? '' : categories}
        </section>
    `;
};

const renderScheduleAccount = (account, options) => {
    const accountKey = String(account.account_id || '');
    const categories = (account.categories || [])
        .map((category) => renderScheduleCategory(account, category, options))
        .filter(Boolean)
        .join('');
    if (!categories) {
        return '';
    }
    const visibleItems = (account.categories || []).flatMap((category) => (
        (category.items || [])
            .filter((item) => item.status !== PLACEMENT_STATUS_EXCLUDED)
            .filter((item) => !options.scheduleVisibleKeysSet || options.scheduleVisibleKeysSet.has(item.item_key))
    ));
    const stats = getScheduleSelectionStats(visibleItems, options);
    const collapsed = options.scheduleCollapsedAccountKeysSet?.has(accountKey);
    const toggleLabel = collapsed
        ? i18next.t('ads:placementSchedule.expandGroup', {defaultValue: 'Expand'})
        : i18next.t('ads:placementSchedule.collapseGroup', {defaultValue: 'Collapse'});
    return `
        <section class="placement-plan-account placement-plan-account--schedule" data-schedule-account-key="${escapeHtml(accountKey)}">
            <header class="placement-schedule-group-header">
                <label class="placement-schedule-group-check">
                    <span class="placement-schedule-check-slot">
                        <input
                            type="checkbox"
                            class="form-check-input ogo-checkbox placement-schedule-account-check"
                            data-schedule-account-key="${escapeHtml(accountKey)}"
                        >
                    </span>
                    <span class="placement-schedule-check-text">
                        <strong>${escapeHtml(account.account_name || account.account_id)}</strong>
                        <span class="placement-plan-muted">
                            ${i18next.t('ads:placementSchedule.groupSelectionCount', {
                                defaultValue: 'Selected: {{selected}} of {{total}}',
                                selected: stats.selected,
                                total: stats.total,
                            })}
                        </span>
                    </span>
                </label>
                <button
                    type="button"
                    class="placement-schedule-collapse-toggle placement-schedule-toggle-account ${collapsed ? '' : 'is-expanded'}"
                    data-schedule-account-key="${escapeHtml(accountKey)}"
                    aria-expanded="${collapsed ? 'false' : 'true'}"
                    aria-label="${escapeHtml(toggleLabel)}"
                    title="${escapeHtml(toggleLabel)}"
                >
                    ${renderPlacementChevronIcon()}
                </button>
            </header>
            ${collapsed ? '' : categories}
        </section>
    `;
};

export const renderPlacementPlan = (plan, {
    viewMode = 'preflight',
    activeFilter = 'all',
    allowExclude = true,
    operationType = '',
    packageSelections = {},
    singlePlacementSelections = {},
    advertisingItemSelections = {},
    scheduledItemStrategies = {},
    scheduledCategoryStrategies = {},
    scheduledPackageSelections = {},
    scheduledPackageSelectionModes = {},
    scheduledPackageMaxPrices = {},
    unpaidDraftItemKeys = [],
    expandedCategoryKeys = null,
    expandedResourceKeys = null,
    expandedPackageSizeKeys = [],
    categoryDisplayNames = {},
    purchasingCategoryKey = '',
    purchaseRefreshRequired = false,
    scheduleRows = [],
    renderScheduleControl = null,
    scheduleVisibleKeys = null,
    scheduleCollapsedAccountKeys = [],
    scheduleCollapsedCategoryKeys = [],
    collapsedPreflightAccountKeys = [],
    collapsedPreflightCategoryKeys = [],
    actionSelectOpenKey = '',
    isScheduled = false,
    resourcePreviewPending = false,
} = {}) => {
    if (!plan || !Array.isArray(plan.accounts)) {
        return `<div class="workspace-empty">${i18next.t('ads:placementPlan.empty', {defaultValue: 'Placement plan will appear here.'})}</div>`;
    }
    const isAdvertisingPlan = plan.resource_kind === PLACEMENT_RESOURCE_ADVERTISING_FEATURE;
    const effectiveActiveFilter = isAdvertisingPlan ? 'all' : activeFilter;
    const options = {
        packageSelections,
        operationType,
        singlePlacementSelections,
        advertisingItemSelections,
        scheduledItemStrategies,
        scheduledCategoryStrategies,
        scheduledPackageSelections,
        scheduledPackageSelectionModes,
        scheduledPackageMaxPrices,
        unpaidDraftItemKeysSet: new Set(unpaidDraftItemKeys || []),
        isScheduled: Boolean(isScheduled) || plan.execution_mode === 'scheduled',
        expandedCategoryKeys,
        expandedResourceKeys,
        expandedPackageSizeKeysSet: new Set(expandedPackageSizeKeys || []),
        categoryDisplayNames,
        purchasingCategoryKey,
        purchaseRefreshRequired: Boolean(purchaseRefreshRequired),
        scheduleRowsByKey: new Map((scheduleRows || []).map((row) => [row.key, row])),
        renderScheduleControl,
        scheduleVisibleKeysSet: Array.isArray(scheduleVisibleKeys) ? new Set(scheduleVisibleKeys) : null,
        scheduleCollapsedAccountKeysSet: new Set(scheduleCollapsedAccountKeys || []),
        scheduleCollapsedCategoryKeysSet: new Set(scheduleCollapsedCategoryKeys || []),
        collapsedPreflightAccountKeysSet: new Set(collapsedPreflightAccountKeys || []),
        collapsedPreflightCategoryKeysSet: new Set(collapsedPreflightCategoryKeys || []),
        actionSelectOpenKey,
        resourcePreviewPending: Boolean(resourcePreviewPending),
    };
    if (viewMode === 'schedule') {
        const accounts = plan.accounts
            .map((account) => renderScheduleAccount(account, options))
            .filter(Boolean)
            .join('');
        const emptyLabel = Array.isArray(scheduleVisibleKeys) && scheduleVisibleKeys.length === 0
            ? i18next.t('ads:placementSchedule.noSearchMatches', {defaultValue: 'No adverts match the search.'})
            : i18next.t('ads:placementPlan.noItemsForFilter', {defaultValue: 'No items match this filter.'});
        return `
            <div class="placement-plan-schedule-groups">
                ${accounts || `<div class="workspace-empty">${emptyLabel}</div>`}
            </div>
        `;
    }
    const accounts = plan.accounts
        .map((account) => renderAccount(account, effectiveActiveFilter, allowExclude, options))
        .filter(Boolean)
        .join('');
    return `
        ${isAdvertisingPlan ? '' : renderSummary(plan, effectiveActiveFilter, resourcePreviewPending)}
        <div class="placement-plan-groups">
            ${accounts || `<div class="workspace-empty">${i18next.t('ads:placementPlan.noItemsForFilter', {defaultValue: 'No items match this filter.'})}</div>`}
        </div>
    `;
};

export const getReadyPlacementItems = (plan) => (
    Array.isArray(plan?.items)
        ? plan.items.filter((item) => item.status === PLACEMENT_STATUS_READY)
        : []
);

export const getDraftPublishPlacementItems = (plan, allowUnpaidPublish = false) => (
    Array.isArray(plan?.items)
        ? plan.items.filter((item) => (
            item.status === PLACEMENT_STATUS_READY
            || (allowUnpaidPublish && item.status === PLACEMENT_STATUS_NEEDS_PACKAGE)
        ))
        : []
);

export const getExecutionItemsFromPlacementPlan = (plan) => {
    const operationType = plan?.operation_type;
    const sourceItems = operationType === PLACEMENT_OPERATION_PUBLISH_DRAFTS
        ? getDraftPublishPlacementItems(plan, false)
        : getReadyPlacementItems(plan);
    return sourceItems.map((item) => {
        if (operationType === PLACEMENT_OPERATION_PUBLISH_DRAFTS) {
            return {
                account_id: Number(item.account_id),
                draft_id: String(item.draft_id),
            };
        }
        return {
            account_id: Number(item.account_id),
            ad_id: String(item.ad_id),
        };
    });
};
